import type { TranslationKey } from "../index";

export const de: Record<TranslationKey, string> = {
    // --- common — short words/phrases reused verbatim across unrelated components ------------------
    "common.brandHomeAriaLabel": "SoundCheckBox — Startseite",
    "common.close": "Schließen",
    "catalogCard.open": "Katalogkarte",
    "catalogCard.openAlternatives": "Katalogkarten der Alternativen",
    "catalogCard.alternatives": "Alternativen",
    "catalogCard.type": "Typ",
    "catalogCard.category": "Kategorie",
    "catalogCard.weight": "Gewicht",
    "catalogCard.power": "Leistung",
    "about.heading": "Über diesen Build",
    "about.web": "Web",
    "about.api": "API",
    "about.schema": "Schema-Vertrag",
    "about.unknown": "unbekannt",
    "about.unstamped": "Build ohne Stempel",
    "about.envProduction": "Produktion",
    "about.envNotProduction": "{env} — keine Produktion",
    "about.mismatch": "Web-Bundle und API stammen aus unterschiedlichen Commits. Das Deployment ist nur halb angekommen: Was Sie sehen und was antwortet, ist nicht derselbe Code.",
    "common.cancel": "Abbrechen",
    "common.delete": "Löschen",
    "ridersList.deletePublished.heading": "Dieser Rider ist veröffentlicht",
    "ridersList.deletePublished.body": "Beim Löschen wird auch der öffentliche Link abgeschaltet. Wer ihn hat — eine Location, ein Verleih, wer den gedruckten QR-Code gescannt hat — kann ihn nicht mehr öffnen, und das lässt sich nicht rückgängig machen.",
    "ridersList.deletePublished.dontAskAgain": "In dieser Sitzung nicht mehr fragen",
    "common.deletePermanentlyPrompt": "Endgültig löschen?",
    "common.yesDelete": "Ja, löschen",
    "common.loading": "Laden…",
    "common.saving": "Speichern…",
    "common.sending": "Senden…",

    // --- side-nav (components/side-nav.ts) ----------------------------------------------------------
    "sideNav.home": "Startseite",
    "sideNav.soundman": "Tontechniker",
    "sideNav.soundmanComingSoon": "Tontechniker — demnächst verfügbar",
    "sideNav.myRiders": "Meine Tech Rider",
    "sideNav.offers": "Angebote",
    "myEquipment.warehouse.heading": "Mein Lager",
    "myEquipment.warehouse.search": "In meinem Lager suchen",
    "myEquipment.warehouse.add": "Zu meinem Lager hinzufügen",
    "myEquipment.warehouse.formed": "Mein Lager ist vollständig — Angebote schlagen zuerst daraus vor",
    "myEquipment.backToOffer": "← Zurück zum Angebot",
    "myEquipment.fromOffer.line": "Aus {offer} zum Rider {rider} zu Ihrem Lager hinzugefügt: {items}.",
    "myEquipment.fromOffer.grow": "Erweitern Sie Ihr Lager:",
    "myEquipment.fromOffer.pick": "Aus unserem Katalog wählen",
    "myEquipment.fromOffer.import": "Eigene Liste importieren",
    "myEquipment.fromOffer.dismiss": "Ausblenden",
    "myEquipment.weight.notSpecified": "Nicht angegeben",
    "myEquipment.col.manufacturer": "Hersteller",
    "myEquipment.sort.by": "Sortieren nach",
    "myEquipment.sort.category": "Kategorie",
    "myEquipment.more": "Mehr",
    "myEquipment.searchClose": "Suche schließen",
    "sideNav.inbox": "Posteingang",
    "sideNav.inboxComingSoon": "Posteingang — demnächst verfügbar",
    "sideNav.outbox": "Postausgang",
    "sideNav.outboxComingSoon": "Postausgang — demnächst verfügbar",
    "sideNav.eventManager": "Event-Manager",
    "sideNav.eventManagerComingSoon": "Event-Manager — demnächst verfügbar",
    "sideNav.warehouse": "Lagerbestand",
    "sideNav.warehouseComingSoon": "Lagerbestand — demnächst verfügbar",
    "sideNav.myProfile": "Mein Profil",
    "sideNav.admin": "Verwaltung",
    "sideNav.designThemeLabel": "Erscheinungsbild",
    "sideNav.languageLabel": "Sprache",
    "sideNav.mainAriaLabel": "Hauptmenü",

    // --- app-header (components/app-header.ts) ------------------------------------------------------
    "appHeader.adminTitle": "Administrator",
    "appHeader.adminBadge": "Admin",
    "appHeader.logoutTitle": "Abmelden",
    "appHeader.logoutLabel": "Abmelden",

    // --- verify-email banner (components/verify-email-banner.ts) -------------------------------------
    "verifyBanner.message": "Ihre E-Mail-Adresse ist noch nicht bestätigt — Speichern, Veröffentlichen und andere Änderungen sind deaktiviert, bis Sie sie bestätigen.",
    "verifyBanner.profileLink": "Zum Profil",
    "verifyBanner.resend": "Bestätigungs-E-Mail erneut senden",
    "verifyBanner.resendSent": "Gesendet ✓",
    "verifyBanner.resendFailed": "Bestätigungs-E-Mail konnte nicht erneut gesendet werden — versuchen Sie es über Ihr Profil erneut.",
    "verifyBanner.dismissAriaLabel": "Schließen",

    // --- write errors shown in place of a raw API error (api/http.ts#isEmailNotVerifiedError) --------
    "errors.emailNotVerified": "Bestätigen Sie Ihre E-Mail-Adresse, bevor Sie Änderungen vornehmen — gehen Sie zu Ihrem Profil, um den Link erneut zu senden.",

    // --- login (pages/login-page.ts) -----------------------------------------------------------------
    "login.hint": "Melden Sie sich an oder erstellen Sie ein Konto, um Technical Rider zu erstellen und zu speichern.",
    "login.hintRegister": "Konto anlegen — E-Mail und Passwort genügen, und Ihre Arbeit kommt mit.",
    "login.emailLabel": "E-Mail oder Benutzername",
    "login.passwordLabel": "Passwort",
    "login.submit": "Anmelden",
    "login.register": "Registrieren",
    "login.consentNotice": "Mit der Erstellung eines Kontos stimmst du unseren {termsLink} und unserer {privacyLink} zu.",
    "login.termsLink": "Nutzungsbedingungen",
    "login.privacyLink": "Datenschutzerklärung",
    "legal.privacyTitle": "Datenschutzerklärung",
    "legal.termsTitle": "Nutzungsbedingungen",
    "legal.unavailable": "Dieses Dokument konnte nicht geladen werden. Bitte versuche es später erneut.",
    "consent.title": "Bitte prüfe unsere Bedingungen",
    "consent.body": "Um SoundCheckBox weiter zu nutzen, stimme bitte unseren {termsLink} und unserer {privacyLink} zu.",
    "consent.changeNote": "Wir können diese Dokumente aktualisieren. Wenn du mit einer Änderung nicht einverstanden bist, kannst du die Nutzung des Dienstes beenden.",
    "consent.agree": "Ich stimme zu",
    "consent.decline": "Abmelden",
    "consent.saveFailed": "Deine Zustimmung konnte nicht gespeichert werden. Bitte versuche es erneut.",
    "editor.publishConfirm": "Dieser Rider wird unter einem öffentlichen Link veröffentlicht, den jeder ohne Konto öffnen kann, und bleibt verfügbar, bis du die Veröffentlichung zurücknimmst. Alles darin — auch Namen, Telefonnummern und E-Mail-Adressen — wird damit öffentlich. Veröffentlichen?",
    "editor.publishImpact.offer": "Dieser Rider steht in einem noch nicht vereinbarten Angebot ({shows}). Eine neue Version ändert dieses Angebot — der Dienstleister beantwortet die geänderten Zeilen erneut. Veröffentlichen?",
    "editor.publishImpact.contract": "Dieser Rider steht unter einem vereinbarten Angebot ({shows}). Dort kann er nicht ohne Änderung des Show-Vertrags geändert werden: Der Dienstleister muss die Differenz bestätigen, und die Crew sieht die neue Version. Veröffentlichen?",
    "editor.publishImpact.unnamedShow": "eine Show ohne Namen",
    "editor.unpublishConfirm": "Das Zurücknehmen der Veröffentlichung deaktiviert ALLE öffentlichen Links zu diesem Rider — diese Version und alle früheren — und wer bereits einen Link hat, kann ihn nicht mehr öffnen. Das lässt sich nicht rückgängig machen: erneutes Veröffentlichen erzeugt einen NEUEN Link. Veröffentlichung zurücknehmen?",
    "editor.publishDirty.heading": "Ungespeicherte Änderungen veröffentlichen?",
    "editor.publishDirty.body": "Veröffentlicht wird die zuletzt gespeicherte Version dieses Riders, und deine letzten Änderungen sind noch nicht gespeichert. Speichere sie zuerst, um sie zu veröffentlichen.",
    "editor.publishDirty.saveAndPublish": "Speichern und veröffentlichen",
    "login.minAge": "Du musst mindestens 16 Jahre alt sein, um ein Konto zu erstellen.",
    "admin.title": "Administrationskonsole",
    "admin.notAvailable": "Diese Seite ist nur für Administratoren verfügbar.",
    "admin.users.title": "Benutzer",
    "admin.users.searchPlaceholder": "Nach E-Mail oder Login suchen",
    "admin.users.searchBtn": "Suchen",
    "admin.users.showAdmins": "Administratoren anzeigen",
    "admin.users.browseAll": "Alle anzeigen",
    "admin.users.prevPage": "Zurück",
    "admin.users.nextPage": "Weiter",
    "admin.users.pageOf": "Seite {page} von {pages} — {total} Konten",
    "admin.users.searchTooShort": "Gib mindestens 2 Zeichen ein.",
    "admin.users.noMatch": "Keine Treffer.",
    "admin.users.colEmail": "E-Mail",
    "admin.users.colLogin": "Login",
    "admin.users.colJoined": "Registriert",
    "admin.users.colVerified": "Bestätigt",
    "admin.users.colAdmin": "Admin",
    "admin.users.colActions": "Aktionen",
    "admin.users.verify": "Als bestätigt markieren",
    "admin.users.grantAdmin": "Zum Admin machen",
    "admin.users.revokeAdmin": "Admin entziehen",
    "admin.users.verified": "E-Mail als bestätigt markiert.",
    "admin.users.granted": "Administratorrechte erteilt.",
    "admin.users.revoked": "Administratorrechte entzogen.",
    "admin.users.confirmGrant": "{user} Administratorrechte erteilen?",
    "admin.users.confirmRevoke": "{user} die Administratorrechte entziehen?",
    "admin.users.deletionPending": "Löschung beantragt",
    "admin.users.tempPassword": "Temporäres Passwort",
    "admin.users.tempPasswordPending": "temporäres Passwort",
    "admin.users.confirmTempPassword": "Ein temporäres Passwort für {user} ausstellen? Das bisherige Passwort funktioniert sofort nicht mehr.",
    "admin.users.tempPasswordIssued": "Temporäres Passwort für {user}. Jetzt kopieren – es lässt sich nicht erneut anzeigen:",
    "admin.users.tempPasswordDone": "Temporäres Passwort ausgestellt.",
    // --- users tab: sort, filters, summary (SCRUM-570) ---
    "admin.users.colLastLogin": "Letzte Anmeldung",
    "admin.users.colRiders": "Rider",
    "admin.users.colLastRiderEdit": "Letzte Rider-Änderung",
    "admin.users.sortBy": "Nach {column} sortieren",
    "admin.users.noLoginRecorded": "Nicht erfasst. Anmeldungen werden erst seit Oktober 2026 gespeichert – davor wurde nichts festgehalten, der Strich heißt also nicht, dass das Konto nie genutzt wurde.",
    "admin.users.noRiderEdit": "Das Konto hat keine Rider, also auch keine Änderung.",
    "admin.users.filtersTitle": "Filter",
    "admin.users.filterAll": "Alle",
    "admin.users.filterRole": "Rolle",
    "admin.users.filterRoleUsers": "Benutzer",
    "admin.users.filterRoleAdmins": "Administratoren",
    "admin.users.filterVerified": "E-Mail bestätigt",
    "admin.users.filterVerifiedYes": "Bestätigt",
    "admin.users.filterVerifiedNo": "Nicht bestätigt",
    "admin.users.filterRiders": "Rider",
    "admin.users.filterRidersWith": "Mit Ridern",
    "admin.users.filterRidersWithout": "Ohne Rider",
    "admin.users.filterActiveDays": "Rider geändert in den letzten … Tagen",
    "admin.users.filterJoinedDays": "Registriert in den letzten … Tagen",
    "admin.users.filterAnyDays": "beliebig",
    "admin.users.clearFilters": "Filter zurücksetzen",
    "admin.stats.title": "Übersicht",
    "admin.stats.filteredHint": "Die Zahlen gelten für die Konten, die zu den Filtern oben passen.",
    "admin.stats.unfilteredHint": "Die Zahlen gelten für alle Konten.",
    "admin.stats.total": "Konten",
    "admin.stats.admins": "Administratoren",
    "admin.stats.verified": "Bestätigt",
    "admin.stats.withRiders": "Mit Ridern",
    "admin.stats.ridersTotal": "Rider insgesamt",
    "admin.stats.windowHeader": "In den letzten…",
    "admin.stats.days7": "7 Tagen",
    "admin.stats.days30": "30 Tagen",
    "admin.stats.days90": "90 Tagen",
    "admin.stats.days365": "365 Tagen",
    "admin.stats.activeRiderAuthors": "Rider geändert",
    "admin.stats.newUsers": "Registriert",
    "admin.stats.loggedIn": "Angemeldet",
    "admin.stats.loginNote": "Anmeldungen werden erst seit Oktober 2026 erfasst. Die Zeile „Angemeldet“ zählt ab diesem Tag; frühere Sitzungen sind nicht enthalten.",
    "admin.erasure.title": "Konten zur Löschung",
    "admin.erasure.hint": "Löschanträge, deren Wartefrist abgelaufen ist. Es wird nichts automatisch gelöscht: Das Löschen ist unumkehrbar und entfernt das Konto samt allen Ridern.",
    "admin.erasure.none": "Nichts fällig.",
    "admin.erasure.colRequested": "Beantragt",
    "admin.erasure.colDue": "Fällig seit",
    "admin.erasure.erase": "Löschen",
    "admin.erasure.confirmPrompt": "Geben Sie {user} ein, um dieses Konto und alle Rider zu löschen. Nicht rückgängig zu machen.",
    "admin.erasure.confirmMismatch": "Die Adresse stimmt nicht — es wurde nichts gelöscht.",
    "admin.erasure.erased": "{user} wurde gelöscht.",
    "admin.content.legalTitle": "Rechtliche Dokumente",
    "admin.content.legalHint": "Ersetzt das mit der Website ausgelieferte Dokument. Leer = die Datei aus dem Repository wird verwendet, genau wie bisher. Markdown.",
    "admin.content.mailTitle": "E-Mail-Texte",
    "admin.content.mailHint": "Ersetzt einzelne Textbausteine in E-Mails, je Sprache. Im Feld steht der aktuell gültige Text — bearbeiten Sie ihn direkt. Unverändertes Speichern behält die ausgelieferte Fassung; „Zurücksetzen“ verwirft Ihre Änderung. Platzhalter in geschweiften Klammern müssen erhalten bleiben — sie werden beim Versand gefüllt.",
    "admin.content.localeLabel": "Sprache",
    "admin.content.usingFile": "ausgelieferte Fassung",
    "admin.content.edited": "hier bearbeitet",
    "admin.content.legalPlaceholder": "Leer — die Website zeigt das Dokument aus dem Repository.",
    "admin.content.colKey": "Schlüssel",
    "admin.content.colText": "Text",
    "admin.content.save": "Speichern",
    "admin.content.revert": "Zurücksetzen",
    "admin.content.saved": "Gespeichert.",
    "admin.content.reverted": "Ausgelieferte Fassung wiederhergestellt.",
    "admin.content.confirmRevert": "Diese Bearbeitung verwerfen und zum ausgelieferten Dokument zurückkehren?",
    "admin.content.uiTitle": "Oberflächentexte",
    "admin.content.uiHint": "Ersetzt einzelne Texte der Oberfläche, je Sprache, ohne neuen Build. Im Feld steht der aktuell gültige Text — bearbeiten Sie ihn direkt. Unverändertes Speichern behält die ausgelieferte Fassung; „Zurücksetzen“ verwirft Ihre Änderung. Änderungen erscheinen nach dem Neuladen.",
    "admin.content.uiSearchPlaceholder": "Nach Schlüssel oder Text suchen",
    "admin.content.uiEditedOnly": "nur bearbeitete",
    "admin.content.uiStart": "Suchbegriff eingeben oder „nur bearbeitete“ anhaken.",
    "admin.content.uiTooMany": "{count} Treffer — die ersten 200 werden gezeigt. Suche eingrenzen.",
    "admin.content.badPlaceholders": "Unbekannte Platzhalter: {list}. Sie würden wörtlich ausgegeben.",
    "admin.content.savedReload": "Gespeichert. Seite neu laden, um es zu sehen.",
    "admin.tabs.users": "Konten",
    "admin.tabs.content": "Texte",
    "admin.tabs.settings": "Einstellungen",
    "admin.content.history": "Verlauf",
    "admin.content.noHistory": "Noch keine früheren Fassungen.",
    "admin.content.restore": "Diese Fassung laden",
    "admin.content.restoredIntoEditor": "In den Editor geladen — prüfen und speichern.",
    "admin.settings.title": "Website-Einstellungen",
    "admin.settings.hint": "Änderungen wirken sofort. Ohne Überschreibung gilt der im Code hinterlegte Wert.",
    "admin.settings.save": "Speichern",
    "admin.settings.reset": "Zurücksetzen",
    "admin.settings.saved": "{key} gespeichert.",
    "admin.settings.wasReset": "{key} steht wieder auf dem Standardwert.",
    "admin.settings.overridden": "geändert (Standard: {default})",
    "admin.settings.isDefault": "Standard",
  "admin.models.title": "Ungenutzte 3D-Modelle",
  "admin.models.hint": "Hochgeladene .glb-Dateien, auf die kein Preset und kein gespeicherter Rider verweist. Das Löschen ist endgültig — es gibt keinen Upload-Verlauf zum Wiederherstellen.",
  "admin.models.scan": "Prüfen",
  "admin.models.scanning": "Prüfe…",
  "admin.models.none": "Nichts aufzuräumen — alle hochgeladenen Modelle werden verwendet.",
  "admin.models.summary": "{count} ungenutzt, {size} insgesamt. {inUse} weiterhin in Verwendung. {presets} Presets und {riders} Rider geprüft.",
  "admin.models.colFile": "Datei",
  "admin.models.colSize": "Größe",
  "admin.models.colModified": "Hochgeladen",
  "admin.models.deleteSelected": "Auswahl löschen",
  "admin.models.deleteSelectedCount": "Auswahl löschen ({count})",
  "admin.models.confirmDelete": "{count} Modelldatei(en) endgültig löschen? Das lässt sich nicht rückgängig machen.",
  "admin.models.deleted": "{count} Datei(en) gelöscht, {size} freigegeben.",
  "admin.models.skipped": "{count} übersprungen — sie sind inzwischen referenziert.",
    "login.forgotPassword": "Passwort vergessen?",
    "login.sessionExpired": "Ihre Sitzung ist abgelaufen — bitte melden Sie sich erneut an.",
    "login.invalidCredentials": "Falsche E-Mail/Benutzername oder falsches Passwort.",

    // --- recovery (pages/recovery-pages.ts) -----------------------------------------------------------
    "recovery.forgotTitle": "Passwort zurücksetzen",
    "recovery.forcedTitle": "Eigenes Passwort festlegen",
    "recovery.forcedLead": "Sie haben sich mit einem vom Support ausgestellten Passwort angemeldet. Legen Sie ein eigenes fest, um fortzufahren – bis dahin ist nichts anderes verfügbar.",
    "recovery.forcedCurrentLabel": "Temporäres Passwort",
    "recovery.forcedSubmit": "Passwort speichern",
    "recovery.forgotLead": "Geben Sie Ihre E-Mail-Adresse ein. Wir senden Ihnen einen Link zum Zurücksetzen Ihres Passports.",
    "recovery.emailLabel": "E-Mail",
    "recovery.forgotSubmit": "Link zum Zurücksetzen senden",
    "recovery.backToSignIn": "Zurück zur Anmeldung",
    "recovery.forgotSuccess": "Wenn zu dieser Adresse ein Konto existiert, wurde ein Link gesendet. Prüfen Sie Ihren Posteingang und Spam-Ordner.",
    "recovery.resetIncompleteTitle": "Der Link ist unvollständig",
    "recovery.resetIncompleteLead": "Diesem Link fehlt der Verifizierungstoken. Fordern Sie einen neuen an.",
    "recovery.resetAgainLink": "Neuen Link anfordern",
    "recovery.resetTitle": "Neues Passwort wählen",
    "recovery.newPasswordLabel": "Neues Passwort",
    "recovery.repeatPasswordLabel": "Neues Passwort wiederholen",
    "recovery.resetSubmit": "Neues Passwort festlegen",
    "recovery.passwordMismatch": "Die beiden Passwörter stimmen nicht überein.",
    "recovery.resetSuccess": "Passwort aktualisiert. Weiterleitung zur Anmeldung…",
    "recovery.verifyTitle": "E-Mail-Adresse Bestätigung",
    "recovery.verifyChecking": "Link wird überprüft…",
    "recovery.continueToSignIn": "Weiter zur Anmeldung",
    "recovery.verifyNoToken": "Diesem Link fehlt der Token. Fordern Sie einen neuen über Ihr Profil an.",
    "recovery.verifySuccess": "Ihre E-Mail-Adresse wurde bestätigt. Sie können sich jetzt anmelden.",
    "recovery.verifyFailed": "Dieser Bestätigungslink ist ungültig oder abgelaufen. Melden Sie sich an und fordern Sie über Ihr Profil einen neuen an.",
    "recovery.verifyChangeTitle": "Bestätigung Ihrer neuen E-Mail-Adresse",
    "recovery.verifyChangeNoToken": "Diesem Link fehlt der Token. Fordern Sie einen neuen über Ihr Profil an.",
    "recovery.verifyChangeSuccess": "Ihre neue E-Mail-Adresse ist bestätigt — melden Sie sich ab jetzt damit an.",
    "recovery.verifyChangeFailed": "Dieser Bestätigungslink ist ungültig oder abgelaufen. Melden Sie sich an und fordern Sie über Ihr Profil eine neue E-Mail-Änderung an.",

    // --- landing (pages/landing-page.ts) --------------------------------------------------------------
    "landing.nav.try": "Ausprobieren",
    "landing.nav.eventManager": "Für Event-Manager",
    "landing.nav.rentCompany": "Für Verleihfirmen",
    "landing.nav.soundman": "Für Tontechniker",
    "landing.nav.howItWorks": "So funktioniert's",
    "landing.nav.contacts": "Kontakt",
    "landing.nav.myRiders": "Meine Tech Rider",
    "landing.nav.login": "Anmelden",
    "landing.nav.signup": "Kostenlos registrieren",
    "landing.hero.title": "Planen Sie Ihre Show",
    "landing.hero.sub": "Online-Service für veranstaltungstechnisches Equipment. Erstellen Sie einen Tech Rider mit allen Details und finden Sie Verleihfirmen mit einem Klick.",
    "landing.hero.cta": "Editor öffnen",
    "landing.hero.mediaAlt": "Der SoundCheckBox 3D Rider Editor",
    "landing.try.title": "Erstellen Sie jetzt einen Rider",
    "landing.try.lead": "Kein Konto nötig. Der Editor öffnet sich sofort, und Ihre Arbeit bleibt in diesem Browser — ein Konto brauchen Sie nur zum Speichern, Teilen oder Versenden.",
    "landing.try.cta": "Editor öffnen",
    "landing.eventManager.title": "Für Event-Manager & Veranstalter",
    "landing.eventManager.body": "Sie organisieren ein Festival, eine Tournee oder ein Firmenevent? SoundCheckBox bündelt alle Vereinbarungen zwischen Bands und Verleihfirmen an einem Ort.",
    "landing.eventManager.feature1Title": "Equipment-Abgleich",
    "landing.eventManager.feature1Desc": "Jede Anforderung im Tech Rider wird mit dem tatsächlichen Bestand der Verleiher abgeglichen.",
    "landing.eventManager.feature2Title": "Angebote vergleichen",
    "landing.eventManager.feature2Desc": "Vergleichen Sie Optionen direkt nebeneinander, statt unzählige E-Mails zu schreiben.",
    "landing.eventManager.feature3Title": "Vereinbarungen nachverfolgen",
    "landing.eventManager.feature3Desc": "Zuverlässigkeit und Bewertungen von Verleihfirmen vor der Buchung einsehen.",
    "landing.eventManager.feature4Title": "Zahlungsabwicklung",
    "landing.eventManager.feature4Desc": "Wickeln Sie Buchungen direkt auf der Plattform ab.",
    "landing.eventManager.mediaAlt": "Workflow für Event-Manager",
    "landing.rentCompany.title": "Für Verleihfirmen & Dry Hire",
    "landing.rentCompany.body": "Ein automatisierter Assistent, der eingehende Rider liest, mit Ihrem Lagerbestand abgleicht und Aufträge automatisch kalkuliert.",
    "landing.rentCompany.feature1Title": "Lagerabgleich",
    "landing.rentCompany.feature1Desc": "Rider werden mit Ihrem echten Bestand im Lager verglichen, nicht mit Wunschlisten.",
    "landing.rentCompany.feature2Title": "Automatische Preiskalkulation",
    "landing.rentCompany.feature2Desc": "Angebote werden sofort anhand Ihrer Mietpreislisten berechnet.",
    "landing.rentCompany.feature3Title": "Große Volumen",
    "landing.rentCompany.feature3Desc": "Entwickelt für große Lagerbestände mit Unterstützung für Software-Integrationen.",
    "landing.rentCompany.feature4Title": "Zumietung / Dry Hire",
    "landing.rentCompany.feature4Desc": "Fehlt ein Gerät? Das System zeigt Ihnen sofort Partner, die das Equipment auf Lager haben.",
    "landing.rentCompany.mediaAlt": "Workflow für Verleihfirmen",
    "landing.soundman.title": "Für Tontechniker & Sound-Engineers",
    "landing.soundman.body": "Weniger Papierkram, mehr Zeit für die Musik. Erstellen Sie Rider aus bewährten Setups und nutzen Sie diese für all Ihre Bands.",
    "landing.soundman.feature1Title": "Erprobte Setups",
    "landing.soundman.feature1Desc": "Starten Sie mit funktionierenden Bühnenplänen statt mit einem leeren Blatt.",
    "landing.soundman.feature2Title": "Eigene Presets",
    "landing.soundman.feature2Desc": "Speichern Sie benutzerdefinierte Equipment-Gruppen einmal ab und nutzen Sie sie immer wieder.",
    "landing.soundman.feature3Title": "Multi-Band-Verwaltung",
    "landing.soundman.feature3Desc": "Verwalten Sie alle betreuten Künstler in einem einzigen Konto.",
    "landing.soundman.feature4Title": "Paketangebote",
    "landing.soundman.feature4Desc": "Verleihfirmen bieten Ihnen komplette Pakete passend zu Ihrem Rider an.",
    "landing.soundman.mediaAlt": "Workflow für Tontechniker",
    "landing.howItWorks.videoTitle": "SoundCheckBox — So funktioniert's",
    "landing.howItWorks.lead": "Plazieren Sie Equipment auf einer 3D-Bühne: Kanalbelegung, Out-List und Equipment-Liste erstellen sich automatisch. Ausdrucken oder direkt an Verleiher senden.",
    "landing.howItWorks.feature1Title": "1. Bühne aufbauen",
    "landing.howItWorks.feature1Desc": "Ziehen Sie Presets — Schlagzeug, Backline, Mikrofone, Monitore — auf eine maßstabsgetreue Bühne.",
    "landing.howItWorks.feature2Title": "2. Automatische Listen",
    "landing.howItWorks.feature2Desc": "Kanäle, Signale und Equipment werden aus den Objekten auf der Bühne abgeleitet.",
    "landing.howItWorks.feature3Title": "3. Teilen",
    "landing.howItWorks.feature3Desc": "Drucken Sie einen sauberen A4-Rider mit Bühnenplan aus oder senden Sie den Link direkt weiter.",
    "landing.contact.title": "Kontaktieren Sie uns",
    "landing.contact.emailLabel": "E-Mail",
    "landing.contact.addressLabel": "Adresse",
    "landing.contact.followUs": "Folgen Sie uns",
    "landing.contact.nameFieldLabel": "Ihr Name",
    "landing.contact.emailFieldLabel": "Ihre E-Mail",
    "landing.contact.messageLabel": "Nachricht",
    "landing.contact.submit": "Nachricht senden",
    "landing.contact.captchaNote": "Geschützt durch Cloudflare Turnstile — es gelten die {privacyLink} und {termsLink} von Cloudflare.",
    "landing.contact.privacyPolicyLinkText": "Datenschutzerklärung",
    "landing.contact.termsLinkText": "Nutzungsbedingungen",
    "landing.contact.validationError": "Bitte geben Sie Ihren Namen, Ihre E-Mail und eine Nachricht ein.",
    "landing.contact.success": "Vielen Dank — Ihre Nachricht wurde gesendet. Wir antworten an die angegebene E-Mail-Adresse.",
    "landing.footer.tagline": "SoundCheckBox — Baukasten für technische Rider",

    // --- riders list (pages/riders-list-page.ts) ------------------------------------------------------
    "riders.pageTitle": "Meine Tech Rider",
    "riders.newRiderButton": "Neuer Rider",
    "riders.filter.groupLabel": "Rider nach Status filtern",
    "riders.filter.all": "Alle",
    "riders.filter.empty": "Keine Rider entsprechen diesem Filter.",
    "riders.table.title": "Titel",
    "riders.table.band": "Band / Künstler",
    "riders.table.created": "Erstellt am",
    "riders.table.changed": "Geändert am",
    "riders.table.status": "Status",
    "riders.table.action": "Aktion",
    "riders.table.empty": "Noch keine Rider vorhanden — klicken Sie auf „+ Neuer Rider“, um den ersten zu erstellen.",
    "riders.status.published": "Veröffentlicht",
    "riders.status.draft": "Entwurf",
    "riders.status.publicLinkTitle": "Öffentlicher Link: {url}",
    "riders.actions.edit": "Bearbeiten",
    "riders.actions.moreActionsAriaLabel": "Weitere Aktionen für {title}",
    "riders.actions.publish": "Veröffentlichen",
    "riders.actions.unpublish": "Veröffentlichung aufheben",
    "riders.actions.print": "Drucken",
    "riders.actions.copyPublicLink": "Öffentlichen Link kopieren",
    "riders.actions.rename": "Umbenennen",
    "riders.actions.duplicate": "Duplizieren",
    "riders.copySuffix": "(Kopie)",
    "riders.copySuffixN": "(Kopie {n})",
    "riders.rename.heading": "Rider umbenennen",
    "riders.rename.nameLabel": "Neuer Name",
    "riders.rename.confirm": "Umbenennen",
    "riders.status.renaming": '„{title}“ wird umbenannt…',
    "riders.status.renamed": 'Umbenannt in „{title}“.',
    "riders.status.renameFailed": '„{title}“ konnte nicht umbenannt werden: {message}',
    "riders.status.duplicating": '„{title}“ wird dupliziert…',
    "riders.status.duplicated": '„{title}“ wurde erstellt.',
    "riders.status.duplicateFailed": '„{title}“ konnte nicht dupliziert werden: {message}',
    "riders.status.deleting": '„{title}“ wird gelöscht…',
    "riders.status.deleted": '„{title}“ gelöscht.',
    "riders.status.deleteFailed": 'Fehler beim Löschen von „{title}“: {message}',
    "riders.status.linkCopied": "Öffentlicher Link kopiert: {url}",
    "riders.status.unpublished": 'Veröffentlichung von „{title}“ aufgehoben.',
    "riders.status.publishSuccess": '„{title}“ veröffentlicht — Link: {url}',
    "riders.status.unpublishFailed": 'Fehler beim Aufheben der Veröffentlichung von „{title}“: {message}',
    "riders.status.publishFailed": 'Fehler beim Veröffentlichen von „{title}“: {message}',
    "riders.status.publishedNoPicture": "Die öffentliche Seite hat kein Bühnenplan-Bild: Diese Liste kann keins zeichnen. Veröffentliche aus dem Editor, um es hinzuzufügen.",
    "riders.status.loading": "Rider werden geladen…",
    "riders.status.loadFailed": "Rider konnten nicht geladen werden: {message}",

    // --- profile (pages/profile-page.ts) ---------------------------------------------------------------
    "profile.pageTitle": "Mein Profil",
    "profile.personal.title": "Persönliche Daten",
    "profile.personal.nameLabel": "Name",
    "profile.personal.phoneLabel": "Telefon",
    "profile.personal.countryLabel": "Land",
    "profile.personal.cityLabel": "Stadt",
    "profile.personal.languageLabel": "Sprache",
    "profile.personal.save": "Persönliche Daten speichern",
    "profile.work.title": "Berufliche Daten",
    "profile.work.companyLabel": "Firma / Firma / Band",
    "profile.work.roleLabel": "Tätigkeit",
    "roleChoice.hint": "Bestimmt, welche Seite sich nach der Anmeldung öffnet.",
    "roleChoice.hintOptional": "Optional. Bestimmt, welche Seite sich nach der Anmeldung öffnet.",
    "profile.work.role.soundman": "Tontechniker",
    "profile.work.role.manager": "Event-Manager",
    "profile.work.role.rental": "Verleihfirma",
    "profile.work.role.other": "Sonstiges",
    "profile.work.save": "Berufliche Daten speichern",
    "profile.defaults.title": "Rider-Standardwerte",
    "profile.defaults.hint": "Diese Angaben füllen den Kontaktblock jedes NEUEN Riders aus. Jeder Rider behält seine eigenen Endwerte.",
    "profile.defaults.bandNameLabel": "Bandname / Künstlername",
    "profile.defaults.contactNameLabel": "Kontaktperson",
    "profile.defaults.contactPhoneLabel": "Telefonkontakt",
    "profile.defaults.contactEmailLabel": "E-Mail-Kontakt",
    "profile.defaults.save": "Standardwerte speichern",
    "profile.security.title": "Sicherheit",
    "profile.security.emailLabel": "E-Mail",
    "profile.security.emailConfirmedLabel": "E-Mail bestätigt",
    "profile.security.passwordChangedLabel": "Passwort zuletzt geändert",
    "profile.security.memberSinceLabel": "Mitglied seit",
    "profile.security.emailVerifiedYes": "Ja",
    "profile.security.emailVerifiedNot": "Noch nicht",
    "profile.security.resendVerification": "Bestätigungs-E-Mail erneut senden",
    "profile.security.changePasswordTitle": "Passwort ändern",
    "profile.security.changePasswordSubmit": "Passwort ändern",
    "profile.security.currentPasswordLabel": "Aktuelles Passwort",
    "profile.security.newPasswordLabel": "Neues Passwort",
    "profile.security.repeatPasswordLabel": "Neues Passwort wiederholen",
    "profile.security.resendSent": "Gesendet ✓",
    "profile.security.resendSuccess": "Bestätigungs-E-Mail gesendet. Prüfen Sie Ihren Posteingang und Spam-Ordner.",
    "profile.security.passwordMismatch": "Die beiden neuen Passwörter stimmen nicht überein.",
    "profile.security.passwordChanged": "Passwort geändert. Wir haben Ihnen eine Bestätigung per E-Mail geschickt.",
    "profile.security.changeEmailTitle": "E-Mail ändern",
    "profile.security.changeEmailHint": "Wir senden einen Bestätigungslink an die neue Adresse — nichts ändert sich, bevor Sie darauf klicken.",
    "profile.security.newEmailLabel": "Neue E-Mail",
    "profile.security.currentPasswordForEmailLabel": "Aktuelles Passwort",
    "profile.security.changeEmailSubmit": "Bestätigungslink senden",
    "profile.security.changeEmailMissingFields": "Geben Sie die neue E-Mail-Adresse und Ihr aktuelles Passwort ein.",
    "profile.security.changePasswordMissingFields": "Geben Sie Ihr aktuelles Passwort und ein neues Passwort mit mindestens 8 Zeichen ein.",
    "profile.security.changeEmailSuccess": "Falls {newEmail} verfügbar ist, ist ein Bestätigungslink bereits unterwegs dorthin. Prüfen Sie Posteingang und Spam-Ordner.",
    "profile.security.changeEmailPending": "Wir haben einen Bestätigungslink an {newEmail} gesendet. Sie sind bis zur Bestätigung weiterhin als {currentEmail} angemeldet.",
    "profile.ai.title": "KI-Assistent",
    "profile.ai.hint": "Der Assistent hat ein kleines kostenloses Kontingent. Danach füge deinen eigenen Google-Gemini-Schlüssel hinzu, um ihn weiter zu nutzen — du zahlst direkt an Google, ohne Limit hier.",
    "profile.ai.usageFree": "Kostenloses Kontingent genutzt: {used} von {cap}.",
    "profile.ai.usageByok": "Dein eigener Schlüssel wird genutzt ({hint}) — kein Limit.",
    "profile.ai.byokHint": "Füge deinen eigenen Gemini-API-Schlüssel ein, um den Assistenten nach dem Freikontingent weiter zu nutzen.",
    "profile.ai.getKeyLink": "Kostenlosen Schlüssel holen",
    "profile.ai.keyLabel": "Dein Gemini-API-Schlüssel",
    "profile.ai.keyReplace": "gib einen neuen Schlüssel ein, um ihn zu ersetzen",
    "profile.ai.saveKey": "Schlüssel speichern",
    "profile.ai.clearKey": "Schlüssel entfernen",
    "profile.ai.byokUnavailable": "Eigener Schlüssel ist auf diesem Server noch nicht verfügbar.",
    "profile.ai.keyRequired": "Gib zuerst deinen API-Schlüssel ein.",
    "profile.ai.keyValidating": "Schlüssel wird geprüft…",
    "profile.ai.keySaved": "Schlüssel gespeichert — der Assistent nutzt ihn jetzt.",
    "profile.ai.keyCleared": "Schlüssel entfernt.",
    "profile.ai.keySaveFailed": "Schlüssel konnte nicht gespeichert werden: {message}",
    "profile.ai.loadFailed": "Deine KI-Nutzung konnte nicht geladen werden.",
    "profile.privacy.title": "Daten & Datenschutz",
    "profile.privacy.hint": "Ihre Rechte gemäß DSGVO und geltenden Datenschutzgesetzen.",
    "profile.privacy.downloadTitle": "Meine Daten herunterladen",
    "profile.privacy.downloadHint": "Alle über Sie gespeicherten Daten einschließlich all Ihrer Rider als eine JSON-Datei.",
    "profile.privacy.downloadButton": "Herunterladen",
    "profile.privacy.marketingTitle": "Produkt-Updates",
    "profile.privacy.marketingHint": "Gelegentliche Neuigkeiten über SoundCheckBox. Wichtige System-E-Mails werden immer zugestellt.",
    "profile.privacy.marketingCheckbox": "Produkt-E-Mails erhalten",
    "profile.privacy.deleteTitle": "Konto löschen",
    "profile.privacy.deleteHint": "Löscht Ihr Konto und alle Ihre erstellten Tech Rider dauerhaft.",
    "profile.privacy.deleteButton": "Konto löschen",
    "profile.privacy.cancelDeleteButton": "Löschung abbrechen",
    "profile.status.loadFailed": "Profil konnte nicht geladen werden: {message}",
    "profile.status.personalSaved": "Persönliche Daten gespeichert.",
    "profile.status.workSaved": "Berufliche Daten gespeichert.",
    "profile.status.defaultsSaved": "Rider-Standardwerte gespeichert.",
    "profile.privacy.preparingExport": "Daten werden vorbereitet…",
    "profile.privacy.exportDownloaded": "Ihre Datendatei wurde heruntergeladen.",
    "profile.privacy.marketingOn": "Sie erhalten Produkt-E-Mails.",
    "profile.privacy.marketingOff": "Sie wurden von den Produkt-E-Mails abgemeldet.",
    "profile.privacy.deleteConfirm": "SoundCheckBox-Konto wirklich löschen?\n\nIhr Konto und alle Rider werden nach der Schonfrist dauerhaft gelöscht. Sie können den Vorgang vor Ablauf dieser Frist auf dieser Seite abbrechen.",
    "profile.privacy.deletionScheduled": "Löschung geplant. Details wurden per E-Mail gesendet — Sie können noch abbrechen.",
    "profile.privacy.deletionCancelled": "Löschung abgebrochen. Ihr Konto bleibt aktiv.",
    "profile.privacy.deletionPendingHint": "Geplante Löschung am {date}. Bis zu diesem Datum jederzeit widerrufbar.",

    // --- public rider viewer (pages/public-viewer-page.ts) ---------------------------------------------
    "publicViewer.headerSub": "Öffentlicher Rider",
    "publicViewer.riderListsAriaLabel": "Rider-Listen",
    "publicViewer.controls.grid": "Gitter",
    "publicViewer.controls.titles": "Titel",
    "publicViewer.controls.inputCh": "Eingang (CH)",
    "publicViewer.controls.outputMix": "Ausgang (MIX)",
    "publicViewer.controls.source": "Quelle",
    "publicViewer.controls.comments": "Bemerkungen",
    "publicViewer.window.close": "Fenster schließen",
    "publicViewer.window.zoomIn": "Vergrößern",
    "publicViewer.window.zoomOut": "Verkleinern",
    "publicViewer.controls.scene": "3D-Bühne",
    "publicViewer.controls.sceneLoading": "3D (wird geladen...)",
    "publicViewer.controls.sceneHint": "Die Bühne räumlich ansehen — zum Umkreisen ziehen.",
    "publicViewer.controls.plan": "Draufsicht",
    "publicViewer.controls.planHint": "Blick senkrecht von oben auf die Bühne, aus Publikumssicht.",
    "publicViewer.controls.schematic": "S/W-Plan",
    "publicViewer.schematicLoading": "Das Blatt wird gezeichnet…",
    "publicViewer.schematicFailed": "Die Symbolbibliothek konnte nicht geladen werden.",
    "publicViewer.controls.schematicHint": "Kontrastreicher Schwarz-Weiß-Bühnenplan für Druck und schnelle Übersicht.",
    "publicViewer.controls.schematicDisabledHint": "Noch nicht fertig — der Schwarz-Weiß-Plan ist noch in Arbeit.",
    "publicViewer.loaderAlt": "Daten und 3D-Modelle werden geladen...",
    "publicViewer.controls.hideStage": "Bühne ausblenden",
    "publicViewer.controls.showStage": "Bühne einblenden",
    "publicViewer.controls.hideStageHint": "Bühne ausblenden, damit Listen und Rider-Text den ganzen Bildschirm füllen.",
    "publicViewer.controls.viewSwitchAriaLabel": "Bühnenansicht",
    "publicViewer.controls.follow": "Auf Auswahl zoomen",
    "publicViewer.controls.followHint": "Beim Wählen eines Kanals fliegt die Kamera zum zugehörigen Objekt.",
    "publicViewer.controls.labels": "Beschriftungen",
    "publicViewer.controls.fit": "Ganze Bühne",
    "publicViewer.controls.fitHint": "Die gesamte Bühne wieder ins Bild holen.",
    "publicViewer.controls.print": "Drucken",
    "publicViewer.stageHint": "Ziehen zum Drehen, Scrollen/Pinchen zum Zoomen, Antippen zum Auswählen.",
    "publicViewer.stageHintPlan": "Ziehen zum Verschieben, Scrollen/Pinchen zum Zoomen, Antippen zum Auswählen.",
    "publicViewer.planPosterAlt": "Bühnenplan, der bei der Veröffentlichung dieses Riders gerendert wurde",
    "publicViewer.deselectAriaLabel": "Auswahl aufheben",
    "publicViewer.tabs.rider": "Rider",
    "publicViewer.tabs.input": "Input",
    "publicViewer.tabs.output": "Output",
    "publicViewer.tabs.gear": "Equipment",
    "publicViewer.bandLogoAlt": "Bandlogo",
    "publicViewer.table.ch": "Ch",
    "publicViewer.table.source": "Quelle",
    "publicViewer.table.type": "Typ",
    "publicViewer.table.name": "Name",
    "publicViewer.table.comment": "Bemerkung",
    "publicViewer.table.category": "Kategorie",
    "publicViewer.table.owner": "Eigentümer",
    "publicViewer.table.count": "Anzahl",
    "publicViewer.footer": "Erstellt mit",
    "publicViewer.meta.inputCh": "Eingangskanäle (CH)",
    "publicViewer.meta.outputMix": "Ausgangskanäle (MIX)",
    "publicViewer.meta.gear": "Equipment",
    "publicViewer.meta.gearCount": "{n} Stück",
    "publicViewer.noRiderNotes": "Keine Anmerkungen zum Rider.",
    "publicViewer.untitledRiderHeading": "Unbenannter Rider",
    "publicViewer.untitledRiderDocTitle": "Tech Rider",
    "publicViewer.documentTitle": "{title} — SoundCheckBox",
    "publicViewer.contactLabel": "Kontakt: {name}",
    "publicViewer.telLabel": "Tel: {phone}",
    "publicViewer.emailLabel": "E-Mail: {email}",
    "publicViewer.status.noRiderId": "Kein Rider angegeben — im Link fehlt die ID.",
    "publicViewer.status.withdrawn": "Der Autor hat diesen Rider zurückgezogen; dieser Link funktioniert nicht mehr.",
    "common.deletePublishedPrompt": "Endgültig löschen? Der öffentliche Link funktioniert dann für niemanden mehr.",
    "common.yesDeleteAndUnpublish": "Ja, löschen und Link abschalten",
    "publicViewer.status.notPublished": "Dieser Rider ist nicht veröffentlicht (oder existiert nicht). Bitten Sie den Ersteller um Veröffentlichung.",
    "publicViewer.powerUnavailable": "Leistungszusammenfassung nicht verfügbar.",
    "publicViewer.controls.makeOffer": "Angebot erstellen",
    "publicViewer.controls.makeOfferHint": "Beantworten Sie die Kanal- und Ausrüstungsliste dieses Riders als Anbieter.",
    "publicViewer.controls.compact": "Kompaktansicht",
    "publicViewer.controls.compactHint": "Der Schwarz-Weiß-Plan mit der kurzen Kanalliste darunter.",
    "publicViewer.controls.io": "I/O",
    "publicViewer.controls.ioHint": "Kanal- und Mix-Nummern im Plan anzeigen.",
    "publicViewer.controls.more": "Mehr",
    "publicViewer.controls.loadingWord": "(wird geladen...)",
    "publicViewer.phantomTitle": "Phantomspeisung (+48V)",
    "publicViewer.planFull.ariaLabel": "Plan im Vollbild öffnen",
    "publicViewer.status.offerOwnRider": "Sie können kein Angebot für Ihren eigenen Rider erstellen.",
    "publicViewer.status.offerStartFailed": "Angebot konnte nicht gestartet werden: {error}",

    // --- offer mode (pages/offer-overlay.ts, PR2 — docs/V2_OFFER_TECH_APPROVAL.md) -----------------------
    "offer.loading": "Angebot wird geladen…",
    "offer.loadFailed": "Dieses Angebot konnte nicht geladen werden: {error}",
    "offer.backToRider": "Zurück zum Rider",
    "offer.status.draft": "Entwurf",
    "offer.status.submitted": "Eingereicht",
    "offer.status.changesRequested": "Änderungen angefordert",
    "offer.status.awaitingOrganiser": "Warten auf Veranstalter",
    "offer.status.approved": "Genehmigt",
    "offer.roundLabel": "Antworten v{n}",
    "offer.commentLabel": "Allgemeiner Kommentar",
    "offer.commentPlaceholder": "Was der Rider-Autor über dieses Angebot insgesamt wissen sollte…",
    "offer.authorCommentHeading": "Der Rider-Autor hat Änderungen angefordert",
    "offer.declinedReasonHeading": "Ablehnungsgrund vom Rider-Autor",
    "offer.markRemainingOk": "Rest als OK markieren",
    "offer.saveDraft": "Entwurf speichern",
    "offer.submit": "Angebot einreichen",
    "offer.saving": "Wird gespeichert…",
    "offer.savedStatus": "Entwurf gespeichert.",
    "offer.submitting": "Wird eingereicht…",
    "offer.submitFailed": "Einreichen fehlgeschlagen: {error}",
    "offer.saveFailed": "Speichern fehlgeschlagen: {error}",
    "offer.printFailed": "Drucken fehlgeschlagen: {error}",
    "offer.guest.notice": "Sie sind nicht angemeldet. Ihre Antworten bleiben in diesem Browser gespeichert, bis Sie das Angebot senden.",
    "offer.myList.button": "Mit meinem Lager abgleichen",
    "offer.myList.empty": "Ihr Lager ist leer – fügen Sie Ihre Technik unter Lagerbestand hinzu",
    "offer.myList.link": "Lagerbestand",
    "offer.myList.summary": "{ok} Zeilen können nach Ihrer Liste als OK markiert werden, bei {short} Zeilen haben Sie weniger als nötig, {missing} stehen nicht in Ihrer Liste",
    "offer.myList.apply": "Anwenden",
    "offer.myList.cancel": "Abbrechen",
    "offer.myList.short": "Sie haben {have} von {need}",
    "offer.myList.marker": "aus meiner Liste",
    "offer.myList.nothing": "Nichts zu prüfen: Jede Gear-Zeile hat bereits eine Antwort.",
    "offer.guest.signInStep": "Zum Senden melden Sie sich an oder erstellen ein Konto — Ihre Antworten bleiben erhalten.",
    "offer.guest.signInButton": "Anmelden oder Konto erstellen",
    "offer.guest.keepEditing": "Weiter bearbeiten",
    "offer.guest.emailLabel": "Ihre E-Mail",
    "offer.guest.emailSend": "Angebot senden",
    "offer.guest.emailHint": "Wir senden Ihnen einen Link per E-Mail. Öffnen Sie ihn, und das Angebot ist gesendet — ohne Passwort.",
    "offer.guest.emailInvalid": "Geben Sie eine gültige E-Mail-Adresse ein.",
    "offer.guest.emailSent": "Wir haben eine E-Mail an {email} gesendet. Öffnen Sie den Link darin — Ihr Angebot wird dann von selbst gesendet. Sie können diesen Tab schließen; Ihre Antworten bleiben 48 Stunden in diesem Browser.",
    "offer.guest.emailSendFailed": "Die E-Mail konnte gerade nicht gesendet werden. Bitte versuchen Sie es in einer Minute erneut.",
    "offer.guest.emailAlreadySent": "Die Bestätigungs-E-Mail wurde bereits an {email} gesendet. Öffnen Sie den Link darin — Ihr Angebot wird dann von selbst gesendet. Sehen Sie auch im Spam-Ordner nach: E-Mails von unbekannten Absendern landen oft dort. Wenn sie nicht da ist, fordern Sie sie in {minutes} Min. erneut an.",
    "offer.guest.emailSentAgain": "Die Bestätigungs-E-Mail wurde bereits an {email} gesendet, und wir haben sie noch einmal geschickt. Öffnen Sie den Link in einer der beiden — Ihr Angebot wird dann von selbst gesendet. Sehen Sie auch im Spam-Ordner nach: E-Mails von unbekannten Absendern landen oft dort.",
    "offer.autoSending": "Ihr Angebot wird gesendet…",
    "offer.autoSent": "Ihr Angebot wurde gesendet.",
    "offer.awaitingEmail": "Wir haben eine E-Mail an {email} gesendet. Öffnen Sie den Link darin — Ihr Angebot wird dann von selbst gesendet.",
    "offer.where.title": "Wann und wo — damit das Angebot in der richtigen Show landet",
    "offer.where.dateLabel": "Datum",
    "offer.where.kind.range": "Zeitraum",
    "offer.where.kind.choice": "Mehrere Termine",
    "offer.where.kind.day": "Ein Tag",
    "offer.where.pick.day": "Tippen Sie auf einen Tag.",
    "offer.where.pick.range": "Tippen Sie den ersten, dann den letzten Tag an.",
    "offer.where.pick.choice": "Tippen Sie jeden möglichen Termin an – mindestens zwei.",
    "offer.where.clear": "Noch kein Datum",
    "offer.where.prevMonth": "Vorheriger Monat",
    "offer.where.nextMonth": "Nächster Monat",
    "offer.where.chips": "Schnellauswahl",
    "offer.where.weekdays": "Mögliche Wochentage",
    "offer.where.chip.thisWeek": "Diese Woche",
    "offer.where.chip.nextWeek": "Nächste Woche",
    "offer.where.chip.lastWeekOf": "Letzte Woche im {month}",
    "offer.where.chip.wholeMonth": "Ganzer Monat",
    "offer.where.possibleDays": "Mögliche Tage: {n}",
    "offer.where.removeDate": "Datum entfernen",
    "offer.where.venue": "Venue",
    "offer.where.city": "Stadt (optional)",
    "offer.where.hint": "Die Band sieht Ihr Angebot in ihrer Show für dieses Datum.",
    "offer.where.error.dayMissing": "Wählen Sie das Datum.",
    "offer.where.error.rangeIncomplete": "Tippen Sie den letzten Tag des Zeitraums an.",
    "offer.where.error.rangeBackwards": "Der Zeitraum endet, bevor er beginnt.",
    "offer.where.error.rangeNoDays": "Alle Tage des Zeitraums sind gestrichen — haken Sie mindestens einen Wochentag an.",
    "offer.where.error.choiceFew": "Wählen Sie mindestens zwei Termine oder wechseln Sie zu „Ein Tag“.",
    "offer.placed.whenVenue": "Ihr Angebot für diese Show ({when}, {venue}) wurde gesendet. Die Veranstalter der Show prüfen es – danke!",
    "offer.placed.when": "Ihr Angebot für diese Show ({when}) wurde gesendet. Die Veranstalter der Show prüfen es – danke!",
    "offer.placed.venue": "Ihr Angebot für diese Show ({venue}) wurde gesendet. Die Veranstalter der Show prüfen es – danke!",
    "offer.placed.none": "Ihr Angebot für diese Show wurde gesendet. Die Veranstalter der Show prüfen es – danke!",
    "offer.showLink.for": "Für: {parts}.",
    "offer.showLink.archived": "Diese Show ist bereits vereinbart — sie nimmt keine weiteren Angebote an.",
    "offer.showLink.gone": "Dieser Show-Link funktioniert nicht — er wurde zurückgezogen oder konnte nicht gelesen werden. Bitten Sie die Band um einen neuen.",
    "offer.send.title": "Dieses Angebot senden?",
    "offer.send.rider": "Rider: {title}, v{version}",
    "offer.send.lines": "Zeilen: {total}",
    "offer.send.comment": "Ihr allgemeiner Kommentar",
    "offer.send.ask": "Bitte prüfen: Datum und Ort angeben oder sagen, dass sie noch nicht feststehen.",
    "offer.send.notSet": "Datum und Ort stehen noch nicht fest",
    "offer.send.notSetOn": "Geht raus mit: Datum und Ort stehen noch nicht fest.",
    "offer.send.or": "oder",
    "offer.send.send": "Senden",
    "offer.send.back": "Zurück zum Angebot",
    "offer.send.close": "Schließen",
    "offer.send.need": "Datum und Ort angeben oder „Datum und Ort stehen noch nicht fest“ antippen.",
    "offer.send.viaLink": "Datum und Ort kommen aus dem Show-Link — es gibt nichts auszufüllen.",
    "offer.said.notSet": "Datum und Ort: stehen noch nicht fest (Antwort des Anbieters)",
    "riders.actions.publishToShow": "In einer Show veröffentlichen",
    "publishShow.title": "In einer Show veröffentlichen",
    "publishShow.hint": "Eine Show hat ein Datum und einen Veranstaltungsort. Wer den Link öffnet, antwortet genau auf diese Show.",
    "publishShow.whenWhere": "Wann und wo",
    "publishShow.rider": "Rider: {title}, v{version}",
    "publishShow.create": "Show und Link erstellen",
    "publishShow.creating": "Show wird erstellt…",
    "publishShow.linkLabel": "Link für Anbieter",
    "publishShow.copy": "Kopieren",
    "share.action": "Senden",
    "share.hint": "Diesen Rider per E-Mail, WhatsApp, Telegram oder einen beliebigen Messenger senden",
    "share.email": "E-Mail",
    "share.whatsapp": "WhatsApp",
    "share.telegram": "Telegram",
    "share.copyLink": "Link kopieren",
    "share.copied": "Link kopiert",
    "share.kind": "technischer Rider",
    "publishShow.copied": "Kopiert",
    "publishShow.close": "Schließen",
    "publishShow.notPublished": "Veröffentlichen Sie zuerst den Rider — eine Show verweist auf eine veröffentlichte Version.",
    "publishShow.failed": "Die Show konnte nicht erstellt werden: {error}",
    "showPage.about": "Über diese Show",
    "showPage.when": "Datum",
    "showPage.venue": "Veranstaltungsort",
    "showPage.rider": "Rider",
    "showPage.riderValue": "{title}, v{version}",
    "showPage.dateOpen": "Datum noch nicht vereinbart",
    "showPage.venueNone": "Veranstaltungsort noch nicht gewählt",
    "showPage.makeOffer": "Angebot abgeben",
    "showPage.archived.past": "Diese Show hat stattgefunden. Diese Seite ist ihr Archiv — sie nimmt keine Angebote mehr an.",
    "showPage.archived.agreed": "Diese Show ist vereinbart. Diese Seite ist ihr Archiv — sie nimmt keine Angebote mehr an.",
    "showPage.repeat": "Diese Show wiederholen",
    "showPage.loadFailed": "Diese Show konnte nicht gelesen werden: {error}",
    "repeatShow.title": "Diese Show wiederholen",
    "repeatShow.hint": "Eine neue Show mit demselben Ort und denselben Acts. Das Original bleibt unverändert.",
    "repeatShow.whenWhere": "Neues Datum",
    "repeatShow.rider.newer": "Der Rider hat eine neuere Version (v{newest}; gespielt wurde v{played})",
    "repeatShow.rider.quiet": "Der Rider hat sich seit der Show nicht geändert (v{played}).",
    "repeatShow.rider.newest": "Die neueste nehmen",
    "repeatShow.rider.keep": "Die gespielte behalten",
    "repeatShow.rider.ask": "Die Band um einen neuen bitten",
    "repeatShow.offers.title": "Angebote",
    "repeatShow.offers.count": "Genehmigte Angebote bei dieser Show: {n}",
    "repeatShow.offers.invite": "Dieselbe Firma erneut einladen",
    "repeatShow.offers.none": "Ohne Angebote beginnen",
    "repeatShow.create": "Neue Show erstellen",
    "repeatShow.creating": "Show wird erstellt…",
    "repeatShow.needAnswer": "Beantworten Sie die Fragen oben, um fortzufahren.",
    "repeatShow.done": "Die neue Show ist erstellt. Hier ist ihr Link für Dienstleister:",
    "repeatShow.failed": "Die Show konnte nicht wiederholt werden: {error}",
    "emailLogin.title": "Anmeldung",
    "emailLogin.checking": "Link wird geprüft…",
    "emailLogin.failed": "Dieser Link ist abgelaufen oder wurde bereits verwendet. Öffnen Sie den Rider erneut und drücken Sie Senden — Ihre Antworten bleiben 48 Stunden in diesem Browser.",
    "emailLogin.noToken": "Dieser Link ist unvollständig. Öffnen Sie ihn erneut aus der E-Mail.",
    "emailLogin.success": "Sie sind angemeldet.",
    "emailLogin.offerFailed": "Das mit Ihrer E-Mail gesendete Angebot konnte nicht zugestellt werden — der Rider wurde vielleicht zurückgezogen. Öffnen Sie ihn erneut und senden Sie es noch einmal.",
    "emailLogin.usedTitle": "Dieser Link wurde bereits verwendet",
    "emailLogin.usedBody": "Wenn Sie damit ein Angebot gesendet haben, ist es angekommen — es liegt im Konto {email}. Wir senden einen neuen Link an dieselbe Adresse: Er öffnet Ihr Konto mit Ihrem Angebot, und dort können Sie ein Passwort festlegen.",
    "emailLogin.usedBodyNoEmail": "Wenn Sie mit diesem Link ein Angebot gesendet haben, ist es angekommen. Geben Sie Ihre E-Mail ein, und wir senden einen neuen Link zu Ihrem Konto.",
    "emailLogin.emailLabel": "Ihre E-Mail",
    "emailLogin.sendNew": "Neuen Link senden",
    "emailLogin.signIn": "Ich habe schon ein Konto — anmelden",
    "emailLogin.newSent": "Wir haben einen neuen Link an {email} gesendet. Öffnen Sie ihn — Sie landen in Ihrem Konto, bei Ihrem Angebot.",
    "setPassword.title": "Legen Sie ein Passwort fest, um später zurückzukommen",
    "setPassword.hint": "Ihr Konto wurde über den Link in Ihrer E-Mail erstellt. Mit einem Passwort können Sie es jederzeit öffnen und Ihr Angebot und die Antwort des Autors sehen.",
    "setPassword.label": "Neues Passwort (mindestens 8 Zeichen)",
    "setPassword.button": "Passwort festlegen",
    "setPassword.tooShort": "Das Passwort muss mindestens 8 Zeichen lang sein.",
    "setPassword.done": "Passwort festgelegt. Melden Sie sich nächstes Mal mit Ihrer E-Mail und diesem Passwort an.",
    "setPassword.failed": "Das Passwort konnte nicht festgelegt werden. Bitte versuchen Sie es erneut.",
    "offer.validation.modelRequired": "Geben Sie für jede Antwort „Ersatz“ ein Modell an, bevor Sie speichern.",
    "offer.validation.reasonRequired": "Geben Sie für jede Antwort „N/V“ einen Grund an, bevor Sie speichern.",
    "offer.readOnlyNotice": "Dieses Angebot ist „{status}“ und kann nicht mehr bearbeitet werden.",
    "offer.table.answer": "Antwort",
    "offer.bandBringsNote": "Wird von der Band mitgebracht",
    "offer.gear.consumables": "Verbrauchsmaterial — aus bestätigten Sets",
    "offer.gear.cablesElsewhere": "Kabel stehen in ihrem eigenen Abschnitt weiter unten.",
    "offer.notConnectedNote": "Nicht angeschlossen",
    "offer.answer.unanswered": "—",
    "offer.answer.ok": "OK",
    "offer.answer.markOk": "Als OK bestätigen",
    "offer.answer.substitute": "Ersatz",
    "offer.answer.bandCarried": "Von der Band mitgebracht",
    "offer.answer.na": "N/V",
    "offer.answer.venueProvided": "Vom Veranstaltungsort",
    "offer.answer.modelPlaceholder": "Modell",
    "offer.alt.warehouseEmpty": "In dieser Rubrik haben Sie nichts",
    "offer.alt.searchCatalogue": "In unserem Katalog suchen",
    "offer.answer.reasonPlaceholder": "Grund",

    "offer.answer.commentPlaceholder": "Kommentar",
    "offer.answer.short.ok": "OK",
    "offer.answer.short.substitute": "Alt",
    "offer.answer.short.bandCarried": "Band",
    "offer.answer.short.na": "N/V",
    "offer.answer.short.venueProvided": "Haus",
    "offer.collapseAll": "Alle einklappen",
    "offer.expandAll": "Alle ausklappen",
    "offer.patchShow": "Patch dieser Show →",
    "offer.confirmedOf": "{done} von {total} bestätigt",
    "offer.summary.confirmed": "bestätigt",
    "offer.summary.alternatives": "mit Alternative",
    "offer.summary.unavailable": "nicht verfügbar",
    "offer.summary.open": "noch offen",
    "offer.summary.nothingYet": "Noch nichts beantwortet",
    "offer.allConfirmed": "Alles bestätigt",
    "offer.sectionToggle": "Diesen Abschnitt öffnen oder schließen",
    "offer.plan.title": "Bühnenplan",
    "offer.plan.io": "I/O",
    "offer.plan.ioShow": "Kanal- und Mix-Beschriftung zeigen",
    "offer.plan.ioHide": "Kanal- und Mix-Beschriftung ausblenden",
    "offer.plan.open": "Tippen für Vollbild",
    "offer.plan.close": "Schließen",
    "offer.plan.zoomIn": "Vergrößern",
    "offer.plan.zoomOut": "Verkleinern",
    "offer.plan.loading": "Bühnenplan wird gezeichnet…",
    "offer.plan.failed": "Der Bühnenplan konnte nicht gezeichnet werden.",
    "offer.wire.checkPatch": "Show-Patch und Kabellängen prüfen →",
    "offer.wire.hint": "Öffnet den Patch-Editor und die Kabelberechnung",
    "offer.wire.longMic": "Lange Mikrofonkabel",
    "offer.wire.shortPatch": "Kurze Patchkabel",
    "offer.detail.each": "Jedes der {n} — wofür es dient",
    "offer.detail.kit": "Kit {name}",
    "offer.detail.kitHide": "Kit {name}",
    "offer.amp.summary": "{n} × LA4X amplified controller · {capacity} output channels · belegt {used} · frei {free}",
    "offer.amp.capacity": "drive capacity pro LA4X: 2 × 115XT HiQ oder 4 × SB18 (L-Acoustics)",
    "offer.amp.channels": "{n} Kan.",
    "offer.amp.twoWay": "2-way active (LF + HF)",
    "offer.amp.oneChannel": "1 output channel",
    "offer.amp.used": "{used}/{capacity} Kan.",
    // --- offer review mode (pages/offer-review-overlay.ts, PR3 — docs/V2_OFFER_TECH_APPROVAL.md) -----
    "offer.review.supplierCommentLabel": "Kommentar des Anbieters",
    "offer.review.noComment": "Kein Kommentar.",
    "offer.review.yourRequestHeading": "Von dir angeforderte Änderungen",
    "offer.review.approvedNotice": "Dieses Angebot wurde genehmigt. Weitere Änderungen sind nicht mehr möglich.",
    "offer.review.approve": "Genehmigen",
    "offer.review.requestChanges": "Änderungen anfordern",
    "offer.review.requestChangesCommentLabel": "Was muss geändert werden?",
    "offer.review.requestChangesCommentPlaceholder": "Sag dem Anbieter, was vor der erneuten Einreichung zu korrigieren ist…",
    "offer.review.sendRequest": "Senden",
    "offer.review.approving": "Wird genehmigt…",
    "offer.review.approveFailed": "Genehmigung fehlgeschlagen: {error}",
    "offer.review.sending": "Wird gesendet…",
    "offer.review.requestFailed": "Senden fehlgeschlagen: {error}",
    "offer.review.commentRequired": "Gib an, was geändert werden muss, bevor du sendest.",
    "offer.review.supplierNameUnknown": "Unbekannter Anbieter",
    "offer.review.historyHeading": "Verlauf",
    "offer.review.history.submitted": "Eingereicht",
    "offer.review.history.changesRequested": "Änderungen angefordert",
    "offer.review.history.approved": "Genehmigt",
    "offer.status.declined": "Abgelehnt",
    "offer.review.decline": "Ablehnen",
    "offer.review.declinedNotice": "Dieses Angebot wurde abgelehnt. Der Lieferant kann es weder ändern noch erneut senden.",
    "offer.review.declineConfirm": "Dieses Angebot ablehnen? Das ist endgültig: Der Lieferant wird benachrichtigt und kann es nicht erneut senden.",
    "offer.review.declineReasonLabel": "Grund (optional)",
    "offer.review.declineReasonPlaceholder": "Der Lieferant sieht ihn in der E-Mail…",
    "offer.review.declineConfirmButton": "Angebot ablehnen",
    "offer.review.declining": "Wird abgelehnt…",
    "offer.review.declineFailed": "Ablehnen fehlgeschlagen: {error}",
    "offer.review.history.declined": "Abgelehnt",

    // --- /offers — the two-sided offer inbox (pages/offers-page.ts, PR3) -----------------------------
    "offers.pageTitle": "Angebote",
    "offers.incomingHeading": "Angebote zu meinen Ridern",
    "offers.mineHeading": "Meine Angebote",
    "offers.incomingEmpty": "Noch niemand hat auf einen deiner veröffentlichten Rider geantwortet.",
    "offers.mineEmpty": "Du hast noch kein Angebot zu einem Rider abgegeben.",
    "offers.table.rider": "Rider",
    "offers.table.supplier": "Anbieter",
    "offers.table.supplierUnknown": "Unbekannter Anbieter",
    "offers.table.status": "Status",
    "offers.table.round": "Runde",
    "offers.table.updated": "Aktualisiert",
    "offers.status.loading": "Wird geladen…",
    "offers.status.loadFailed": "Angebote konnten nicht geladen werden: {error}",

    // --- editor topbar (components/topbar.ts) -----------------------------------------------------------
    "topbar.loggedInAs": "Angemeldet als {email}",
    "topbar.adminBadge": "Admin",
    "topbar.adminBadgeTitle": "Sie besitzen Administratorrechte (z. B. 3D-Objekt-Upload).",
    "topbar.unsavedRiderId": "(Neuer ungespeicherter Rider)",
    "topbar.save.title": "Rider in Datenbank speichern ({mod}+S)",
    "topbar.save.copyTitle": "Als eigene Kopie speichern — dieser Rider gehört jemand anderem; Speichern legt einen NEUEN Rider in Ihrem Konto an ({mod}+S)",
    "topbar.save.copyLabel": "Als eigene Kopie speichern",
    "topbar.saveAs.title": "Speichern unter… — speichert den geöffneten Rider als NEUEN Rider unter einem Namen deiner Wahl; das Original bleibt unberührt ({mod}+Shift+S)",
    "topbar.saveAs.label": "Speichern unter…",
    "topbar.attach.title": "Objekt an ein anderes anbinden — aktivieren und danach auf das Eltern-Objekt klicken",
    "topbar.attach.label": "Objekt anbinden",
    "topbar.attach.armedTitle": "Anbinde-Modus aktiv — klicken Sie auf das Eltern-Objekt in der 3D-Szene (Esc zum Abbrechen).",
    "topbar.attach.armedLabel": "Ziel auswählen…",
    "topbar.publish.label": "Rider veröffentlichen",
    "topbar.publish.titleUnsaved": "Rider veröffentlichen — bitte zuerst speichern",
    "topbar.publish.titleNotMine": "Rider veröffentlichen — nur der Eigentümer kann diesen Rider veröffentlichen",
    "topbar.publish.titlePublished": "Veröffentlicht als v{version} — Link: {url} (Klicken zum Aufheben der Veröffentlichung)",
    "topbar.publish.unpublishLabel": "Veröffentlichung aufheben",
    "topbar.publish.titleDefault": "Rider veröffentlichen — schreibgeschützten Link generieren (für QR-Code auf dem Ausdruck)",

    // --- editor page chrome (pages/editor-page.ts, EDITOR_PAGE_HTML template) ----------------------------
    "editor.riderIdTitle": "Gespeicherte Rider-ID (Leer = neuer Rider)",
    "editor.menuToggleTitle": "Menü einklappen/ausklappen",
    "editor.menuToggleLabel": "Menü",
    "editor.overflowMenuTitle": "Mehr",
    "editor.rightPanelCollapseTitle": "Panel einklappen",
    "editor.rightPanelExpandTitle": "Panel ausklappen",
    "editor.rightPanelResizerTitle": "Ziehen, um die Panelbreite zu ändern (Doppelklick setzt sie automatisch zurück)",
    "editor.undoTitle": "Rückgängig ({mod}+Z)",
    "editor.undoLabel": "Rückgängig",
    "editor.redoTitle": "Wiederholen ({mod}+Shift+Z)",
    "editor.redoLabel": "Wiederholen",
    "editor.rotateTitle": "Drehen (R) — Drehring aktivieren und ziehen. Dreht standardmäßig das ganze Set; Alt halten für Einzelobjekt.",
    "editor.rotateLabel": "Drehen",
    "editor.attachArmedHint": "Klicke das Objekt an, an das „{title}“ angehängt wird – Podest, Stativ · Esc bricht ab",
    "editor.modifierAltHint": "Alt — nur dieses Objekt",
    "editor.modifierAltStackHint": "Alt — der ganze Stapel",
    "editor.modifierShiftHint": "Shift — Objekte anklicken zum Gruppieren, loslassen zum Abschließen (oder beim Ziehen über einem Objekt loslassen zum Anheften)",
    "editor.uploadObjectTitleDisabled": "3D-Objekt hochladen — nur für Admins",
    "editor.uploadObjectTitleAdmin": "3D-Objekt hochladen — neues globales Preset aus einer .glb-Datei erstellen",
    "editor.uploadObjectLabel": "3D-Objekt hochladen",
    "editor.deleteObjectLabel": "Ausgewähltes Objekt löschen",
    "editor.deleteObjectTitle": "Ausgewähltes Objekt löschen (Alt+Delete)",
    "editor.savePresetLabel": "Als Preset speichern",
    "editor.deleteSubtreeLabel": "Objekt mit allen untergeordneten Elementen löschen",
    "editor.deleteSubtreeTitle": "Objekt mit allen untergeordneten Elementen löschen (Delete)",
    "editor.selectMode.groupLabel": "Ganzer Stapel",
    "editor.selectMode.groupTitle": "Auswahl: der ganze Stapel — Ziehen, Drehen und Löschen betreffen die Gruppe (Alt halten für ein einzelnes Objekt)",
    "editor.selectMode.itemLabel": "Einzelnes Objekt",
    "editor.selectMode.itemTitle": "Auswahl: einzelnes Objekt — Ziehen, Drehen und Löschen betreffen das angeklickte Objekt (Alt halten für den ganzen Stapel)",
    "editor.deleteInGroupLabel": "Ganzen Stapel löschen",
    "editor.deleteInGroupTitle": "Ganzen Stapel löschen (Delete) — Alt: nur das angeklickte Objekt",
    "editor.deleteInItemLabel": "Dieses Objekt löschen",
    "editor.deleteInItemTitle": "Angeklicktes Objekt löschen (Delete) — Alt: den ganzen Stapel",
    "editor.deleteGroupConfirm.heading": "Ganzen Stapel löschen?",
    "editor.deleteGroupConfirm.body": "„{title}“ wird mitsamt allem entfernt, was darauf steht. Objekte: {n}.",
    "editor.deleteGroupConfirm.confirmLabel": "Stapel löschen",
    "editor.showAnnotationsLabel": "Beschriftungen anzeigen/ausblenden",
    "editor.annotationOptionsTitle": "Anzeigeoptionen für Beschriftungen",
    "editor.annotationContentAriaLabel": "Beschriftungsinhalt",
    "editor.labelsPlaqueStrip": "LABELS",
    "editor.annotationCh": "CH",
    "editor.topViewTitle": "Draufsicht",
    "editor.topViewLabel": "Draufsicht",
    "editor.planViewTitle": "Planblatt",
    "editor.planViewLabel": "Planblatt",
    "editor.view3dLabel": "3D",
    "editor.view3dTitle": "3D-Ansicht",
    "editor.topViewShort": "Top",
    "editor.planViewShort": "Plan",
    "editor.viewSwitcherAriaLabel": "Ansicht",
    "editor.viewSwitcherOptionsTitle": "Ansichtsoptionen",
    "editor.planViewLoading": "Das Blatt wird gezeichnet…",
    "editor.planViewFailed": "Die Symbolbibliothek konnte nicht geladen werden.",
    "editor.status.planMoveRefused": "Dieses Objekt lässt sich dorthin nicht verschieben.",
    "presetMenu.editMode": "Bearbeiten",
    "presetMenu.editModeDone": "Fertig",
    "presetMenu.reorderFailed": "Reihenfolge konnte nicht gespeichert werden: {error}",
    "presetMenu.setDefaultOrder": "Als Standardreihenfolge festlegen",
    "presetMenu.defaultOrderSaved": "Das ist jetzt die Standardreihenfolge für alle, die keine eigene festgelegt haben.",
    "editor.annotationSource": "Quelle",
    "editor.annotationMix": "MIX",
    "editor.annotationTitle": "Titel",
    "editor.annotationDesc": "Beschreibung",
    "editor.showGridLabel": "Koordinatengitter anzeigen/ausblenden",
    "editor.printLabel": "Rider drucken",
    "editor.printTitle": "Als PDF exportieren und drucken",
    "editor.exitLabel": "Zurück zu „Meine Rider“",
    "editor.riderOperationsAriaLabel": "Rider-Operationen",
    "editor.tabs.properties": "Properties",
    "editor.tabs.propertiesTitle": "Properties (E)",
    "editor.tabs.rider": "Rider",
    "editor.tabs.inputList": "Input List",
    "editor.tabs.inputListTitle": "Input List (I)",
    "editor.tabs.outputList": "Output List",
    "editor.tabs.outputListTitle": "Output List (O)",
    "editor.tabs.gearList": "Gear List",
    "editor.riderTab.heading": "Rider-Einstellungen",
    "editor.riderTab.titleLabel": "Titel",
    "editor.riderTab.commentLabel": "Bemerkung",
    "editor.riderTab.stageWidthLabel": "Breite (m)",
    "editor.riderTab.stageDepthLabel": "Tiefe (m)",
    "editor.riderTab.useBorderLabel": "Bühnenbegrenzung aktivieren",
    "editor.riderTab.useBorderHint": "(aus = erlaubt Objekte außerhalb der Bühne — z.B. FOH, Deko)",
    "editor.riderTab.contactLegend": "Kontakt & Band",
    "editor.riderTab.moduleRider": "Rider",
    "editor.riderTab.moduleStage": "Bühne",
    "editor.riderTab.moduleBand": "Band",
    "editor.riderTab.moduleContact": "Kontakt",
    "editor.riderTab.contactHint": "(wird aus dem Profil vorausgefüllt; mit diesem Rider gespeichert)",
    "editor.riderTab.bandNameLabel": "Bandname",
    "editor.riderTab.bandLogoAlt": "Bandlogo",
    "editor.riderTab.bandLogoFileLabel": "Logo-Datei",
    "editor.riderTab.removeLogo": "Logo entfernen",
    "editor.riderTab.contactNameLabel": "Kontaktperson",
    "editor.riderTab.contactPhoneLabel": "Telefonkontakt",
    "editor.riderTab.contactEmailLabel": "E-Mail-Kontakt",
    "editor.riderTab.saveDefaultsTitle": "Kontaktdaten als persönliche Standardwerte speichern — füllt jeden neuen Rider aus",
    "editor.riderTab.saveDefaultsLabel": "Als meine Standardwerte speichern",
    "editor.riderTab.riderTextLabel": "Rider-Text / Anmerkungen",
    "editor.riderTab.savedDefaultsStatus": "Als Standardwerte gespeichert.",
    "editor.riderTab.savedDefaultsFailed": "Standardwerte konnten nicht gespeichert werden: {message}",
    "editor.listHint": "(Gesamter Rider — Zeile zum Umsortieren ziehen; Ch = Position. Zeile anklicken zum Auswählen des Objekts.)",
    "editor.reorderAriaLabel": "Umsortieren",
    "editor.table.ch": "Ch",
    "editor.table.source": "Quelle",
    "editor.table.type": "Typ",
    "editor.table.chName": "Kanalname",
    "editor.table.micDi": "Mikro / DI",
    "editor.table.out": "OUT",
    "editor.table.comment": "Bemerkung",
    "editor.table.name": "Name",
    "editor.table.category": "Kategorie",
    "editor.table.owner": "Eigentümer",
    "editor.table.weight": "Gewicht",
    "editor.table.count": "Anzahl",
    "editor.inputTab.heading": "Input List (Eingänge)",
    "editor.inputTab.addButton": "Eingangskanal hinzufügen",
    "editor.outputTab.heading": "Output List (Ausgänge)",
    "editor.outputTab.addButton": "Ausgangszeile hinzufügen",
    "editor.gearTab.heading": "Gear List (Equipment)",
    "editor.gearTab.hint": "(Gesamter Rider — Equipment jedes Objekts. Zeile anklicken zum Auswählen des Objekts.)",
    "editor.gearTab.addButton": "Equipment-Eintrag hinzufügen",
    "editor.params.closeTitle": "Auswahl aufheben / Schließen",
    "editor.params.emptyHint": "Select an object on the stage to see its properties.",
    "editor.params.heading": "Ausgewähltes Objekt",
    "editor.params.nameLabel": "Modellname",
    "editor.params.nameHint": "(Katalog-Key, schreibgeschützt)",
    "editor.params.titleLabel": "Titel",
    "editor.params.titleToSourceLabel": "Titel als Signalquelle übernehmen",
    "editor.params.commentLabel": "Bemerkung",
    "editor.params.parentLabel": "Eltern-Objekt",
    "editor.params.xLabel": "X",
    "editor.params.yLabel": "Y",
    "editor.params.zLabel": "Z",
    "editor.params.zTitle": "Höhe über der Bühne (m). Für dekorative Objekte außerhalb der Bühnenebene, z. B. eine Leinwand oder Vorhänge.",
    "editor.params.turnLabel": "Drehwinkel°",
    "editor.params.lockPositionLabel": "Position sperren",
    "editor.params.lockChildrenLabel": "Kinder sperren",
    "editor.params.structureLabel": "Bühnenelement / Podest",
    "editor.params.structureHint": "(andere Objekte können darauf platziert werden)",
    "editor.params.heightLabel": "Höhe, m",
    "editor.params.heightHint": "(Oberflächenhöhe)",
    "editor.params.riserAcrossLabel": "Breite, m",
    "editor.params.riserDeepLabel": "Tiefe, m",
    "editor.params.riserHeightsLabel": "Schnellhöhen, m",
    "editor.params.riserResizeLabel": "Auf der Bühne verschieben und skalieren",
    "presetMenu.riserTooltipHint": "Doppelklick zum Ändern der Größe",
    "editor.params.riserResizeDoneLabel": "Fertig",
    "editor.params.riserResizeHint": "Objekt ziehen = verschieben, Griffe ziehen = Größe ändern. Umschalt = frei, Eingabe oder Esc = fertig.",
    "editor.params.stageMoveLabel": "Auf der Bühne verschieben",
    "editor.params.stageMoveHint": "Auf der Bühne ziehen, um es zu verschieben. Umschalt = frei, Eingabe oder Esc = fertig.",
    "editor.params.anchoredHint": "Fixiert — Doppelklick zum Verschieben oder Skalieren",
    "editor.params.anchoredMoveHint": "Fixiert — Doppelklick zum Verschieben",
    "editor.params.anchoredLockedHint": "Gesperrt — „Position sperren“ aufheben, um es zu verschieben",
    "editor.params.snapToGridLabel": "Am Raster ausrichten",
    "editor.params.snapNeedsGridHint": "Das Einrasten funktioniert nur bei sichtbarem Raster (Rasterknopf in der oberen Leiste). Bei ausgeblendetem Raster bewegt sich alles frei.",
    "editor.params.snapToGridHint": "Kanten rasten auf dem Raster ein, das sich aus dem Aufbau ergibt: Podestplatte 0,5 m beim Podest, Cabinet bei der LED-Wand, Standardlänge bei der Traverse. Aus: der Gegenstand bleibt genau dort, wo Sie ihn hinstellen.",
    "riser.handle.deep": "Ziehen, um die Tiefe zu ändern",
    "riser.handle.across": "Ziehen, um die Breite zu ändern",
    "riser.handle.corner": "Ziehen, um Breite und Tiefe zu ändern",
    "riser.handle.height": "Ziehen, um die Höhe zu ändern (0,2-m-Schritte)",
    "riser.handle.dock": "Identisches Podest auf dieser Seite anfügen",
    "riser.readout": "{across} × {deep} m · H {height} m",
    "stage.handle.deep": "Ziehen, um die Bühnentiefe zu ändern",
    "stage.handle.across": "Ziehen, um die Bühnenbreite zu ändern",
    "stage.handle.corner": "Ziehen, um Bühnenbreite und -tiefe zu ändern",
    "stage.readout": "Bühne {across} × {deep} m",
    "riser.status.docked": "Ein identisches Podest wurde daneben angefügt.",
    "ledWall.handle.cols": "Ziehen, um Spalten von Kabinetten hinzuzufügen oder zu entfernen",
    "ledWall.handle.rows": "Ziehen, um Reihen von Kabinetten hinzuzufügen oder zu entfernen",
    "construction.led.cabinetLabel": "Kabinett, mm",
    "construction.led.cabinetCustom": "{w} × {h} (eigene)",
    "construction.led.colsLabel": "Spalten",
    "construction.led.rowsLabel": "Reihen",
    "construction.led.pitchLabel": "Pixelabstand, mm",
    "construction.led.mountLabel": "Montage",
    "construction.led.mountGround": "Am Boden gestapelt",
    "construction.led.mountFlown": "Geflogen",
    "construction.led.bottomLabel": "Unterkante, m",
    "construction.led.contentLabel": "Anzeige",
    "construction.led.contentOff": "Aus (schwarz)",
    "construction.led.contentGrid": "Kabinett-Raster",
    "construction.led.contentBars": "Farbbalken",
    "construction.led.contentLogo": "Logo-Tafel",
    "construction.led.contentImage": "Bild",
    "construction.led.contentMedia": "Medien",
    "construction.led.chooseImage": "Bild wählen…",
    "construction.led.removeImage": "Bild entfernen",
    "construction.led.imageSet": "Bild auf der LED-Wand gesetzt ({w} × {h} px).",
    "construction.led.imageTooBig": "Das Bild ist auch verkleinert zu groß. Bitte ein kleineres wählen.",
    "construction.led.imageUnreadable": "Diese Datei konnte nicht als Bild gelesen werden.",
    "construction.led.imageRemoved": "Bild von der LED-Wand entfernt.",
    "construction.led.mediaLabel": "Medien",
    "construction.led.mediaLogo": "SoundCheckBox-Logo",
    "construction.led.mediaMascot": "Maskottchen",
    "construction.led.mediaIntro": "Intro",
    "construction.led.mediaIntroPlain": "Intro (ohne Geld)",
    "construction.led.mediaTutorial": "Tutorial-Video",
    "construction.led.mediaCustomUpload": "Hochgeladen: {name}",
    "construction.led.mediaCustomLink": "Link: {host}",
    "construction.led.mediaUrlLabel": "Direkter Link (https)",
    "construction.led.mediaUrlPlaceholder": "https://…/video.mp4",
    "construction.led.mediaUrlUse": "Link anzeigen",
    "construction.led.mediaUpload": "Video oder Bild hochladen…",
    "construction.led.mediaChecking": "Link wird geprüft…",
    "construction.led.mediaUploading": "Wird hochgeladen…",
    "construction.led.mediaSet": "Die Wand zeigt jetzt deine Auswahl.",
    "construction.led.mediaProblemEmpty": "Füge einen Link zu einer Video- oder Bilddatei ein.",
    "construction.led.mediaProblemStreaming": "{site}-Seiten können nicht auf der Wand gezeigt werden: {site} liefert Video nur im eigenen Player. Nimm einen direkten Link zur Videodatei (.mp4 oder .webm) oder lade die Datei hoch.",
    "construction.led.mediaProblemNotHttps": "Der Link muss mit https:// beginnen.",
    "construction.led.mediaProblemCredentials": "Entferne Benutzername und Passwort aus dem Link.",
    "construction.led.mediaProblemNotAddress": "Das ist keine Webadresse. Füge den vollständigen Link ein, der mit https:// beginnt.",
    "construction.led.mediaProblemTooLong": "Dieser Link ist zu lang.",
    "construction.led.mediaFailCors": "Diese Website erlaubt es nicht, ihr Video auf anderen Seiten zu zeigen.",
    "construction.led.mediaFailUnreachable": "Die Adresse hat nicht geantwortet. Prüfe den Link und die Verbindung.",
    "construction.led.mediaFailHttp": "Die Adresse hat mit einem Fehler geantwortet ({status}): Die Datei ist nicht da oder nicht öffentlich.",
    "construction.led.mediaFailNotMedia": "Unter dieser Adresse liegt eine Webseite oder eine Stream-Liste, keine Video- oder Bilddatei. Nötig ist ein direkter Link auf eine .mp4-, .webm-, .png-, .jpg- oder .gif-Datei.",
    "construction.led.mediaFailFormat": "Der Browser konnte diese Datei nicht abspielen. Geeignet sind mp4-Video (H.264) oder webm sowie png-, jpeg- oder gif-Bilder.",
    "construction.led.mediaUploadForbidden": "Nur Administratoren können Medien hochladen.",
    "construction.led.mediaUploadType": "Auf die Wand können nur mp4-, webm-, png-, jpeg- und gif-Dateien.",
    "construction.led.mediaUploadEmpty": "Die Datei ist leer.",
    "construction.led.mediaUploadTooBig": "Die Datei ist zu groß: Videos bis {video} MB, Bilder bis {image} MB.",
    "construction.led.mediaUploadFailed": "Das Hochladen ist fehlgeschlagen. Versuche es noch einmal.",
    "construction.led.derivedSize": "{size} m",
    "construction.led.derivedPx": "{px} px",
    "construction.led.derivedCount": "Kabinette: {count}",
    "construction.led.derivedLoad": "ca. {kg} kg · ca. {kw} kW max.",
    "construction.truss.typeLabel": "Typ",
    "construction.truss.typeLadder": "Leiter",
    "construction.truss.typeTriangle": "Dreieck",
    "construction.truss.typeBox": "Viereck",
    "construction.truss.profileLabel": "Profil, mm",
    "construction.truss.lengthLabel": "Länge, m",
    "construction.truss.trimLabel": "Trimhöhe (Unterkante), m",
    "construction.truss.supportLabel": "Abhängung",
    "construction.truss.supportFlown": "Geflogen (Kettenzüge)",
    "construction.truss.supportFloor": "Am Boden",
    "construction.truss.supportGoalpost": "Torbogen (zwei Türme)",
    "construction.truss.sections": "Segmente: {sections}",
    "construction.truss.towers": "Türme 2 × {h} m",
    "construction.truss.longSpan": "langes Feld — Lasttabelle prüfen",
    "editor.params.listsInputs": "Eingänge",
    "editor.params.listsOutputs": "Ausgänge",
    "editor.params.listsGear": "Technik",
    "editor.params.moduleObject": "Objekt",
    "editor.params.modulePosition": "Position",
    "editor.params.listPosition": "Position",
    "editor.params.listPositions": "Positionen",
    "editor.params.actionsLegend": "Objekt-Aktionen",
    "editor.params.rotateHint": "Nutzen Sie das Werkzeug <strong>Drehen</strong> in der oberen Leiste oder geben Sie einen genauen <strong>Drehwinkel</strong> oben ein.",
    "editor.params.addChildLabel": "Kind-Objekt hinzufügen",
    "editor.params.deleteObjectLabel": "Objekt löschen",
    "editor.params.savePresetTitle": "Dieses Objekt mit allen Unterelementen als Preset speichern",
    "editor.params.savePresetLabel": "Als Preset speichern",
    "editor.params.addInputLabel": "Neuen Eingang hinzufügen",
    "editor.params.addOutputLabel": "Neuen Ausgang hinzufügen",
    "editor.params.linkOutputLabel": "Mit Ausgang verknüpfen",
    "editor.params.linkOutputTitle": "Dieses Objekt mit einer vorhandenen Ausgangsleitung oder Kanalnummer verknüpfen",
    "editor.params.linkOutputDisabledTitle": "Keine weiteren Ausgänge auf der Bühne zum Verknüpfen vorhanden",
    "editor.params.addGearLabel": "Neues Equipment hinzufügen",
    "editor.presetCategoriesAriaLabel": "Preset-Kategorien",
    "editor.exitConfirm.title": "Ohne Speichern beenden?",
    "editor.exitConfirm.body": "Sie haben ungespeicherte Änderungen. Wenn Sie jetzt beenden, gehen diese verloren.",
    "editor.exitConfirm.saveAndLeave": "Speichern und beenden",
    "editor.exitConfirm.leaveWithoutSaving": "Ohne Speichern beenden",

    // --- editor page behaviour (pages/editor-page.ts, JS-built status messages) ---------------------------
    "editor.status.gluedToGroup": "Der Gruppe hinzugefügt — {members} Objekte bewegen sich jetzt gemeinsam.",
    "editor.status.glueNoTarget": "Unter dem Ablagepunkt ist nichts — Umschalt über einem Objekt halten, um dessen Gruppe beizutreten.",
    "editor.status.glueFailed": "Konnte der Gruppe nicht beitreten: {reason}",
    "editor.status.detached": "Aus der Gruppe gelöst — bewegt sich jetzt eigenständig.",
    "editor.status.detachNotGrouped": "Dieses Objekt gehört zu keiner Gruppe.",
    "editor.status.multiSelectCount": "{count} zum Gruppieren ausgewählt — Shift loslassen zum Abschließen.",
    "editor.status.grouped": "{count} Objekte gruppiert.",
    "editor.status.groupFailed": "Gruppieren fehlgeschlagen: {reason}",
    "editor.status.attachCancelled": "Anbinden abgebrochen.",
    "editor.status.attachFailed": "Anbinden fehlgeschlagen: {reason}",
    "editor.status.attached": "{source} an {target} angebunden.",
    "editor.status.undo": "Rückgängig gemacht.",
    "editor.status.redo": "Wiederhergestellt.",
    "editor.status.attachArmed": "Anbinde-Modus aktiv — klicken Sie auf das Eltern-Objekt für {name} (Esc zum Abbrechen).",
    "editor.status.savingCopy": "Eigene Kopie wird gespeichert…",
    "editor.status.savedAsCopy":
        "Als NEUER Rider gespeichert — Ihre eigene Kopie. Das Original gehört jemand anderem und bleibt unverändert.",
    "editor.status.saved": "Um {time} gespeichert.",
    "editor.status.guestDraftRestored": "Deine letzte Arbeit ist wieder da — sie liegt in diesem Browser, bis du sie in einem Konto speicherst.",
    "editor.status.guestDraftDiscarded":
      "Die in diesem Browser gespeicherte Arbeit konnte nicht sicher geöffnet werden und wurde daher verworfen, statt es erneut zu versuchen. Start mit einem neuen, leeren Rider.",
    "editor.status.guestDraftNotSaved":
      "Dein Browser konnte deine Änderungen hier nicht speichern (der Speicher ist möglicherweise voll). Lass diesen Tab geöffnet oder melde dich an und speichere, um deine Arbeit zu behalten.",
    "editor.draft.offer": "Nicht gespeicherte Änderungen von {time}.",
    "editor.draft.continue": "Diese Änderungen wiederherstellen",
    "editor.draft.discard": "Geöffnetes behalten",
    "editor.draft.close": "Schließen — später entscheiden",
    "editor.naming.offer": "Gespeichert. Dieser Rider heißt noch «New rider» und die Band hat keinen Namen — danach suchen Sie ihn später.",
    "editor.naming.name": "Jetzt benennen",
    "editor.naming.later": "Später",
    "editor.draft.offerDiverged": "Du hast nicht gespeicherte Änderungen von {time}, aber dieser Rider wurde danach auf dem Server erneut gespeichert. Sie darüber wiederherzustellen würde neuere Arbeit überschreiben.",
    "editor.draft.openAsCopy": "Meine Änderungen als Kopie öffnen",
    "editor.draft.useSaved": "Gespeicherte Version verwenden",
    "editor.draft.restored": "Deine nicht gespeicherten Änderungen sind zurück. Drücke Speichern, um sie zu behalten.",
    "editor.draft.restoredAsCopy": "Deine Änderungen sind als neuer, nicht gespeicherter Rider geöffnet. Das gespeicherte Original ist unberührt. Drücke Speichern, um die Kopie zu behalten.",
    "editor.draft.notKept": "Dein Browser konnte keine Wiederherstellungskopie deiner Änderungen anlegen (der Speicher ist möglicherweise voll). Drücke Speichern, damit sie bei einer Abmeldung nicht verloren gehen.",
    "editor.draft.crashed": "Die in diesem Browser gehaltenen nicht gespeicherten Änderungen ließen sich nicht sicher öffnen und wurden daher verworfen, statt es erneut zu versuchen.",
    "editor.status.modelsUnavailable": "Einige 3D-Modelle konnten nicht geladen werden — Objekte erscheinen als beschriftete Kästen. Deine Arbeit ist sicher.",
    "editor.status.presetsUnavailable": "Der Objektkatalog konnte nicht geladen werden — zeichnen geht weiter, aber das Preset-Menü ist leer. Neu laden, um es erneut zu versuchen.",
    "editor.status.guestSaveNeedsAccount": "Melde dich an oder registriere dich, um diesen Rider zu behalten — deine Arbeit bleibt erhalten und kommt mit.",
    "editor.status.guestSaveFailed": "Speichern im Konto fehlgeschlagen: {message}. Deine Arbeit ist noch da — drücke Speichern, um es erneut zu versuchen.",
    "editor.status.saveFailed": "Speichern fehlgeschlagen: {message}",
    "editor.saveAs.heading": "Als neuen Rider speichern",
    "editor.saveAs.nameLabel": "Name",
    "editor.saveAs.confirm": "Kopie speichern",
    "editor.saveAs.hint": "Der geöffnete Rider bleibt unverändert — hier wird eine separate Kopie unter dem angegebenen Namen gespeichert.",
    "editor.saveAs.untitled": "Rider ohne Titel",
    "editor.status.savedAsNamedCopy": '„{title}“ als neuer Rider gespeichert. Der geöffnete Rider ist unverändert.',
    "editor.status.unpublished": "Veröffentlichung aufgehoben — alle öffentlichen Links für diesen Rider sind nicht mehr erreichbar.",
    "editor.status.published": "Veröffentlicht als v{version} — Link: {url}",
    "editor.status.publishedNoPicture": "Veröffentlicht, aber das Planbild konnte nicht erstellt werden — erneut veröffentlichen, ohne diesen Tab zu verlassen, dann sieht der Leser die Bühne während die Modelle laden.",
    "editor.status.copiedSuffix": " (in Zwischenablage kopiert)",
    "editor.status.publishFailed": "Veröffentlichung fehlgeschlagen: {message}",
    "publishProgress.publishing": "Rider wird veröffentlicht…",
    "publishProgress.drawing": "Als v{version} veröffentlicht. Der Bühnenplan für die öffentliche Seite wird gezeichnet…",
    "publishProgress.done": "Als v{version} veröffentlicht — die öffentliche Seite ist fertig, samt Bühnenplan.",
    "publishProgress.published": "Als v{version} veröffentlicht — die öffentliche Seite ist fertig.",
    "publishProgress.noPicture": "Als v{version} veröffentlicht, aber der Bühnenplan konnte nicht gezeichnet werden. Leser sehen die Bühne, sobald ihre Modelle geladen sind. Veröffentliche erneut, ohne diesen Tab zu verlassen, um das Bild hinzuzufügen.",
    "publishProgress.failed": "Veröffentlichen fehlgeschlagen: {message}",
    "publishProgress.open": "Öffentliche Seite öffnen",
    "editor.status.ioKeptNote": " Die Kanäle bleiben als leere reservierte Zeilen erhalten.",
    "editor.status.ioOptimizedNote": " Kanäle wurden entfernt und die Nummerierung aufschließend angepasst.",
    "editor.status.deletedWithChildren": "„{title}“ gelöscht; {count} untergeordnete(s) Objekt(e) behalten und neu zugeordnet.",
    "editor.status.deleted": "„{title}“ gelöscht.",
    "editor.status.deletedSubtree": "„{title}“ und alle untergeordneten Elemente wurden gelöscht.",
    "editor.status.rotated": "„{name}“ um {deg}° gedreht ({mode}).",
    "editor.status.rotateModeObject": "nur Objekt; Kinder behalten Ausrichtung",
    "editor.status.rotateModeSet": "gesamte Gruppe",
    "editor.status.moved": "„{name}“ nach X {x}, Y {y} verschoben.",
    "editor.status.dropMissed": "Außerhalb der Bühne platziert — versuchen Sie es näher an der Podestfläche.",
    "editor.status.loadingRider": "Rider wird geladen…",
    "editor.status.loaded": "„{title}“ geladen — {count} Objekte auf der Bühne.",
    "editor.status.loadedNotMineSuffix":
        " Dieser Rider gehört jemand anderem — Sie können ihn bearbeiten, aber beim Speichern wird Ihre EIGENE Kopie angelegt.",
    "editor.status.loadFailed": "Laden fehlgeschlagen: {message}",
    "editor.status.newRider": "Neuer Rider (noch nicht gespeichert).",

    // --- object-params panel behaviour (components/object-params-panel.ts) -------------------------------
    "objectParams.status.reparentFailed": "Eltern-Objekt konnte nicht geändert werden: {reason}",
    "objectParams.status.moved": "„{name}“ unter {location} verschoben.",
    "objectParams.status.locationNewParent": "ein neues Eltern-Objekt",
    "objectParams.status.locationTopLevel": "die oberste Ebene",
    "objectParams.status.lockedChildren": "Position aller untergeordneten Objekte von „{name}“ gesperrt.",
    "objectParams.status.reseated": "{n} Objekt(e) auf „{name}“ neu platziert.",
    "objectParams.status.addedInput": "Eingangskanal zu „{name}“ hinzugefügt.",
    "objectParams.status.addedOutput": "Ausgangszeile zu „{name}“ hinzugefügt.",
    "objectParams.status.addedGear": "Equipment-Eintrag zu „{name}“ hinzugefügt.",
    "objectParams.status.addedChild": "„{childName}“ zu „{name}“ hinzugefügt.",
    "objectParams.status.removed": "„{name}“ entfernt. Kanäle verbleibender Objekte unverändert.",
    "objectParams.noParentOption": "(Kein Eltern-Objekt — oberste Ebene)",

    // --- preset menu (components/preset-menu.ts) ----------------------------------------------------------
    "presetMenu.loading": "Presets werden geladen…",
    "presetMenu.loadFailed": "Der Objektkatalog konnte nicht geladen werden. Zum erneuten Versuch neu laden.",
    "presetMenu.countSuffix": "({count})",
    "presetMenu.searchPlaceholder": "Katalog durchsuchen",
    "presetMenu.searchNoMatch": "Nichts passt zu „{q}“",
    "presetMenu.noMatch": "Keine Presets für diesen Filter gefunden.",
    "presetMenu.onlyMine": "Nur meine",
    "presetMenu.added": 'Preset „{name}“ zur Bühne hinzugefügt.',
    "presetMenu.removeFromCategory": "Aus {category} entfernen",
    "presetMenu.removedFromCategory": "„{name}“ aus {category} entfernt. Das Preset selbst bleibt unverändert.",
    "presetMenu.removeFromCategoryFailed": "„{name}“ konnte nicht aus {category} entfernt werden: {message}",
    "presetMenu.deleteLastCategoryPrompt": "Die einzige Kategorie. Löschen entfernt das Preset endgültig und lässt sich nicht rückgängig machen. Löschen?",
    "presetMenu.deleteAlsoFromPrompt": "Löscht das Preset endgültig — es verschwindet auch aus {others} und lässt sich nicht rückgängig machen. Löschen?",
    "presetMenu.deleting": 'Preset „{name}“ wird gelöscht…',
    "presetMenu.deleted": 'Preset „{name}“ gelöscht.',
    "presetMenu.deleteFailed": 'Fehler beim Löschen des Presets „{name}“: {message}',
    "presetMenu.uncategorizedCategory": "Ohne Kategorie",
    "presetMenu.chooseCategoryPlaceholder": "— Kategorie zuweisen —",
    "presetMenu.assignedCategory": 'Preset „{name}“ der Kategorie „{category}“ zugewiesen.',
    "presetMenu.assignCategoryFailed": 'Kategorie konnte dem Preset „{name}“ nicht zugewiesen werden: {message}',

    // --- Patch Editor (components/patch-editor-dialog.ts) — SCRUM-241 ---------------------------------------
    "patchEditor.title": "Patch-Editor & Bühnenverkabelung",
    "patchEditor.subtitle": "Physisches Routing für {count} Audiokanäle über Bühnen-Dropboxes",
    "patchEditor.stageBoxesHeading": "Digitale Stageboxen & Racks",
    "patchEditor.routingTableHeading": "Kanal-Patch-Tabelle",
    "patchEditor.colChannel": "Kanal",
    "patchEditor.colSource": "Quelle",
    "patchEditor.colSubSnake": "Sub-Snake / Dropbox",
    "patchEditor.colSocket": "Buchse",
    "patchEditor.colStageBox": "Stagebox",
    "patchEditor.colBoxInput": "Rack-Eingang",
    "patchEditor.colCableLength": "Kabel",
    "patchEditor.cableSummaryHeading": "Kabelliste (Kofferspezifikation)",
    "patchEditor.printSheet": "Patch-Plan drucken",
    "patchEditor.close": "Schließen",
    "patchEditor.done": "Fertig",
    "topbar.patchEditor": "Patch",
    "topbar.patchEditor.disabledTooltip": "Patch-Editor — Verfügbar im Show-/Tour-Modus, wenn der Rider mit einer Spielstätte und Stageboxen verknüpft ist.",
    "topbar.patchEditor.activeTooltip": "Patch-Editor öffnen (P)",

    // --- I/O keep-on-delete dialog (components/io-delete-dialog.ts) ----------------------------------------
    "ioDelete.heading": "Kanalnummern behalten oder Liste aufrücken?",
    "channelOverflow.heading": "Nicht genug freie Kanäle",
    "channelOverflow.summary": "„{preset}“ benötigt {need} Eingangskanäle. Frei sind {free} ({slots}) — {short} fehlen.",
    "channelOverflow.movingLine": "CH {ch} — {source} / {name}",
    "channelOverflow.movingMore": "{lines} … und {n} weitere",
    "channelOverflow.nothingToMove": "Oberhalb der freien Kanäle steht nichts — ein Verschieben bewegt nichts.",
    "channelOverflow.appendLabel": "Am Ende anfügen (CH {channels})",
    "channelOverflow.shiftLabel": "Ab CH {from} um {n} verschieben",
    "channelOverflow.cancelled": "„{name}“ wurde nicht hinzugefügt — Sie haben die Kanalfrage abgebrochen.",
    "ioDelete.optimizeLabel": "Kanäle aufrücken",
    "ioDelete.keepLabel": "Unverändert lassen",
    "ioDelete.rememberLabel": "Auswahl merken (nicht mehr fragen)",
    "ioDelete.inputChannelSingular": "{n} Eingangskanal",
    "ioDelete.inputChannelPlural": "{n} Eingangskanäle",
    "ioDelete.outputChannelSingular": "{n} Ausgangskanal",
    "ioDelete.outputChannelPlural": "{n} Ausgangskanäle",
    "ioDelete.summary": "Das zu löschende Objekt enthält {parts}.",

    // --- row-edit dialog (components/row-edit-dialog.ts) — docs/V2_IO_KEEP_ON_DELETE.md §3c --------------
    "rowEdit.headingInput": "Eingangskanal bearbeiten",
    "rowEdit.headingOutput": "Ausgangszeile bearbeiten",
    "rowEdit.sourceLabel": "Quelle",
    "rowEdit.chNameLabel": "Kanalname",
    "rowEdit.typeLabel": "Typ",
    "rowEdit.commentLabel": "Kommentar",
    "rowEdit.lockedLabel": "Gesperrt",
    "rowEdit.chHashLabel": "Ch #",
    "rowEdit.lockedHint":
      "Der Kanal einer gesperrten Zeile kann nicht geändert werden. Deaktivieren Sie Gesperrt, um sie zu verschieben, und speichern Sie dann — Entsperren und Verschieben geschehen gemeinsam, in einem Speichervorgang.",
    "rowEdit.lockedHintInput": "Die Nummer eines gesperrten Kanals kann nicht geändert werden. Deaktivieren Sie Gesperrt, um ihn zu verschieben, und speichern Sie dann — Entsperren und Verschieben geschehen gemeinsam, in einem Speichervorgang.",
    "rowEdit.linkedLabel": "Ebenfalls verbunden (gemeinsamer Mix)",
    "rowEdit.linkedHint": "Weitere Monitore, die aus demselben Mix gespeist werden — z. B. zwei Wedges an einem Weg. Sie zeigen die Kanalnummer dieser Leitung ebenfalls im Bühnenplan.",
    "rowEdit.linkedEmpty": "Noch keine weiteren Monitore auf der Bühne.",
    "rowEdit.saveLabel": "Speichern",
    "rowEdit.deleteChannel": "Kanal löschen",
    "rowEdit.deleteLine": "Zeile löschen",
    "rowEdit.errorInvalidChannel": "Der Kanal muss eine Zahl ab 1 sein.",
    "rowEdit.errorChannelTaken": "Kanal {channel} wird bereits von einer anderen Zeile verwendet — wählen Sie eine freie Nummer.",
    "rowEdit.errorLineGone": "Diese Zeile existiert nicht mehr — schließen Sie dieses Fenster und versuchen Sie es erneut.",
    "rowEdit.errorChannelGone": "Dieser Kanal existiert nicht mehr — schließen Sie dieses Fenster und versuchen Sie es erneut.",

    // --- link to existing output dialog (components/link-output-dialog.ts) ------------------
    "linkOutput.heading": "Mit vorhandenem Ausgang verknüpfen",
    "linkOutput.currentlyLinkedLabel": "Aktuell verknüpfte Ausgänge:",
    "linkOutput.selectOutputLabel": "Ausgangsleitung auswählen:",
    "linkOutput.chooseOutputOption": "Vorhandenen Ausgang wählen...",
    "linkOutput.manualOption": "Kanalnummer manuell eingeben...",
    "linkOutput.manualChannelLabel": "Kanalnummer:",
    "linkOutput.submitLabel": "Verknüpfen",
    "linkOutput.unlinkButtonTitle": "Diesen Ausgang trennen",
    "linkOutput.ownerSuffix": "Besitzer dieses Ausgangs",
    "linkOutput.alreadyLinkedSuffix": "bereits verknüpft",
    "linkOutput.errorSelectChannel": "Bitte wählen Sie einen Ausgang oder geben Sie eine gültige Kanalnummer ein.",
    "linkOutput.error.invalidChannel": "Geben Sie eine Kanalnummer ab 1 ein.",
    "linkOutput.error.nodeNotFound": "Dieses Objekt steht nicht mehr auf der Bühne. Schließen Sie das Fenster und wählen Sie es erneut aus.",
    "linkOutput.error.alreadyOwner": "Dieser Ausgang gehört dem Objekt bereits — es gibt nichts zu verbinden.",
    "linkOutput.error.channelNotFound": "Auf diesem Kanal gibt es noch keinen Ausgang. Legen Sie ihn zuerst mit „Add new Output“ an.",
    "linkOutput.errorGeneric": "Verknüpfung mit Ausgang fehlgeschlagen.",
    "linkOutput.status.linked": "{name} mit Ausgang {channel} verknüpft",
    "linkOutput.status.unlinked": "{name} von Ausgang {channel} getrennt",

    // --- save-preset dialog (components/save-preset-dialog.ts) ---------------------------------------------
    "savePreset.heading": "Als Preset speichern",
    "savePreset.nameLabel": "Preset-Name",
    "savePreset.categoryLabel": "Kategorie",
    "savePreset.categoryRequiredHint": "(erforderlich)",
    "savePreset.saveAsLabel": "Speichern unter",
    "savePreset.iconLegend": "Icon",
    "savePreset.renderingHint": "Objektbild wird gerendert…",
    "savePreset.useRenderedIcon": "3D-Schnappschuss verwenden",
    "savePreset.useOriginalIcon": "Original-Preset-Icon verwenden",
    "savePreset.clearUploaded": "Hochgeladene Datei löschen",
    "savePreset.submitLabel": "Preset speichern",
    "savePreset.usingUploadedImage": "Ihr hochgeladenes Bild wird verwendet.",
    "savePreset.usingOriginalPresetIcon": "Original-Preset-Bild wird verwendet.",
    "savePreset.autoGenerated": "Automatisch aus dem 3D-Modell generiert — Datei hochladen zum Überschreiben.",
    "savePreset.iconPreviewAlt": "Preset-Icon-Vorschau",
    "savePreset.chooseCategoryOption": "— Kategorie auswählen —",
    "savePreset.newPresetOption": "Neues Preset",
    "savePreset.overwriteOption": "Überschreiben: {title}",
    "savePreset.sharedLibrarySuffix": " (gemeinsame Bibliothek)",
    "savePreset.confirmOverwriteButton": "Ja, überschreiben",
    "savePreset.backToEdit": "Zurück",
    "savePreset.overwriteWarning":
      'Das Überschreiben ersetzt "{title}" — jede zukünftige Einfügung verwendet Ihre neue Version. Rider, die es bereits verwendet haben, behalten ihre eigene Kopie vom Zeitpunkt des Hinzufügens und ändern sich nicht.',
    "savePreset.overwriteWarningSharedNote": "Dieses Preset gehört zur gemeinsamen Bibliothek — Ihre Änderung ist für alle sichtbar.",
    "savePreset.subject": "„{title}“ — {count} Objekt(e), wird mit der gesamten Baumstruktur gespeichert.",
    "savePreset.renderFailed": "Icon konnte nicht gerendert werden — Preset wird ohne Icon gespeichert.",
    "savePreset.errorNodeGone": "Das gewählte Objekt existiert nicht mehr — Fenster schließen und Objekt neu auswählen.",
    "savePreset.errorNoCategory": "Kategorie wählen — ein Preset ohne Kategorie erscheint nicht im Menü.",
    "savePreset.errorStaleTarget": "Sie können dieses Preset nicht mehr überschreiben — Fenster erneut öffnen und versuchen.",
    "savePreset.updated": 'Preset „{title}“ (ID {id}) aktualisiert — Änderungen sind sofort aktiv.',
    "savePreset.saved": 'Preset „{title}“ (ID {id}) gespeichert — im Menü verfügbar.',
    "savePreset.saveFailed": "Preset konnte nicht gespeichert werden: {message}",
    "savePreset.imageReadFailed": "Bild konnte nicht gelesen werden: {message}",

    // --- KI-Assistent (components/ai-assistant-dialog.ts) ---
    "ai.buttonTitle": "KI-Assistent – beschreibe eine Änderung in Worten",
    "ai.buttonLabel": "KI",
    "ai.heading": "KI-Assistent",
    "ai.subheading": "Beschreibe eine Änderung an deiner Bühne in normaler Sprache. Du prüfst jede Änderung, bevor sie angewendet wird.",
    "ai.promptLabel": "Deine Anfrage",
    "ai.promptPlaceholder": "z. B. füge hinten mittig ein Schlagzeug hinzu und links davon einen Bassverstärker",
    "ai.generate": "Generieren",
    "ai.record": "Sprechen",
    "ai.recordTitle": "Sprich deine Anfrage (bis zu 20 s)",
    "ai.recording": "Aufnahme… tippe aufs Mikrofon zum Stoppen.",
    "ai.stopRecording": "Aufnahme stoppen",
    "ai.micUnsupported": "Spracheingabe wird in diesem Browser nicht unterstützt.",
    "ai.micDenied": "Mikrofonzugriff wurde verweigert — erlaube ihn im Browser, um die Sprache zu nutzen.",
    "ai.micEmpty": "Kein Audio aufgenommen — bitte erneut versuchen.",
    "ai.generating": "Denke nach…",
    "ai.previewHeading": "Vorgeschlagene Änderungen",
    "ai.apply": "Änderungen übernehmen",
    "ai.discard": "Verwerfen",
    "ai.emptyPrompt": "Bitte zuerst eingeben, was du ändern möchtest.",
    "ai.noChanges": "Der Assistent hat keine Änderungen vorgeschlagen. Formuliere deine Anfrage anders.",
    "ai.notConfigured": "Der KI-Assistent ist auf diesem Server noch nicht aktiviert.",
    "ai.capReached": "Dein kostenloses KI-Kontingent ist aufgebraucht. Füge in deinem Profil deinen eigenen Gemini-Schlüssel hinzu, um den Assistenten weiter zu nutzen.",
    "ai.providerError": "Der KI-Dienst ist vorübergehend nicht verfügbar. Bitte versuche es gleich noch einmal.",
    "ai.invalid": "Der Assistent konnte für diesen Rider keine gültige Änderung erzeugen. Formuliere es anders.",
    "ai.invalidReference": "Der Assistent hat sich auf ein Objekt oder Preset bezogen, das nicht auf dieser Bühne ist. Benenne es anders oder füge es zuerst hinzu.",
    "ai.someSkipped": "{count} Änderung(en) konnten nicht angewendet werden (sie bezogen sich auf etwas, das nicht auf der Bühne ist, oder passten nicht) und wurden übersprungen:",
    "ai.op.add": "Ein Objekt hinzufügen",
    "ai.op.move": "Ein Objekt verschieben",
    "ai.op.rotate": "Ein Objekt drehen",
    "ai.op.delete": "Ein Objekt löschen",
    "ai.op.settings": "Bühneneinstellungen ändern",
    "ai.op.unknown": "Ein Vorgang",
    "ai.failed": "Der KI-Assistent ist nicht erreichbar. Prüfe deine Verbindung und versuche es erneut.",
    "ai.applied": "{count} Änderung(en) des Assistenten übernommen.",
    "ai.diff.addPreset": "{preset} bei ({x}, {y}) m hinzufügen",
    "ai.diff.moveObject": "{object} nach ({x}, {y}) m verschieben",
    "ai.diff.rotateObject": "{object} um {deg}° drehen",
    "ai.diff.deleteObject": "{object} löschen",
    "ai.diff.deleteTree": "{object} und seine {count} untergeordneten Objekte löschen",
    "ai.diff.setRiderMeta": "Rider-Einstellungen ändern: {fields}",
    "ai.diff.metaField.title": "Titel",
    "ai.diff.metaField.comment": "Kommentar",
    "ai.diff.metaField.width": "Bühnenbreite",
    "ai.diff.metaField.length": "Bühnentiefe",

    // --- upload-object dialog (components/upload-object-dialog.ts) -----------------------------------------
    "uploadObject.heading": "3D-Objekt hochladen",
    "uploadObject.hint": "Nur für Admins. Erstellt ein neues globales Preset aus einer glTF-Datei (.glb).",
    "uploadObject.nameLabel": "Name",
    "uploadObject.modelLabel": "Modell-Datei (.glb)",
    "uploadObject.categoryLabel": "Kategorie",
    "uploadObject.iconLabel": "Icon (optional)",
    "uploadObject.heightLabel": "Höhe, m",
    "uploadObject.structureLabel": "Ist ein Bühnenelement (Podest/Riser, auf dem Objekte stehen können)",
    "uploadObject.submitLabel": "Hochladen & Preset erstellen",
    "uploadObject.errorNameRequired": "Name ist erforderlich.",
    "uploadObject.errorModelRequired": "Bitte wählen Sie eine .glb-Modell-Datei.",
    "uploadObject.errorNotGlb": "Das Modell muss eine .glb-Datei sein.",
    "uploadObject.errorNoCategory": "Bitte wählen Sie eine Kategorie.",
    "uploadObject.uploading": "Wird hochgeladen…",
    "uploadObject.uploaded": '„{name}“ hochgeladen — jetzt in der Preset-Bibliothek verfügbar.',

    // --- list tables (components/list-tables.ts, editor/dom-utils.ts) --------------------------------------
    "dom.deleteLineTitle": "Diese Zeile löschen",
    "dom.editLineTitle": "Diese Zeile bearbeiten",
    "dom.editChannelTitle": "Diesen Kanal bearbeiten",
    "dom.deleteChannelTitle": "Diesen Kanal löschen",
    "listTables.unconnectedRowTitle": "Nicht verbunden — Objekt dieses Kanals wurde gelöscht; Nummer bleibt reserviert. Zeile löschen zum Freigeben.",
    "listTables.unconnectedChannelTitle": "Nicht verbunden — Objekt dieses Kanals wurde gelöscht; Nummer bleibt reserviert. Löschen, um die Nummer freizugeben.",
    "listTables.phantomTitle": "Phantomspeisung (+48V) — zum Umschalten anklicken",
    "listTables.dragHandleTitle": "Ziehen zum Umsortieren — Position bestimmt die Kanalnummer. Strg/Cmd oder Shift halten für Mehrfachauswahl.",
    "listTables.clearSelectionTitle": "Klicken, um die Auswahl aufzuheben",
    "listTables.columnGripTitle": "{name} — Kante ziehen, um die Breite zu ändern; Doppelklick auf die Kante passt automatisch an",
    "listTables.panelGripTitle": "Ziehen, um die Panelbreite zu ändern — dieselbe Kante wie am Panel selbst",
    "listTables.noneOption": "(keine)",
    "listTables.categoryFromCatalogue": "Was das ist, sagt der Katalog. Zum Ändern das Modell ändern, nicht die Beschriftung.",
    "listTables.categoryStale": "Diese Zeile verweist auf Katalogrubrik {ref}, die es nicht mehr gibt. Modell erneut wählen.",
    "listTables.gearUngrouped": "Zu keinem Set gehörig",
    "listTables.selectionBadge": "{count} ausgewählt — ziehen, um die Gruppe zu verschieben",
    "listTables.linkLabel": "Verknüpfen",
    "listTables.unlinkLabel": "Trennen",
    "listTables.pickModeLabel": "Mehrere wählen",
    "listTables.linkTitle": "Die zwei gewählten Zeilen als Stereopaar verknüpfen",
    "listTables.unlinkTitle": "Das gewählte Paar trennen (die Namen bleiben unverändert)",
    "listTables.linkNeedTwo": "Zwei benachbarte Zeilen wählen, um sie als Stereopaar zu verknüpfen",
    "listTables.linkNotAdjacent": "Nur benachbarte Zeilen lassen sich verknüpfen (Kanal N und N+1)",
    "listTables.linkAlreadyPaired": "Eine dieser Zeilen ist schon in einem Paar — zuerst trennen",
    "listTables.unlinkNothing": "Ein verknüpftes Paar wählen, um es zu trennen",
    "listTables.pairMarkTitle": "Verknüpftes Stereopaar — Kanäle {a} und {b}",
    "ioDelete.pairSummary": "Diese Zeile ist die Hälfte eines Stereopaars. Die andere Hälfte bleibt und wird zu einer einzelnen (Mono-)Zeile.",
    "listTables.powerApprox": '≈{watts} W pro Gerät — ungefähre Katalog-Übereinstimmung („{name}“), tatsächlicher Verbrauch kann abweichen.',
    "listTables.powerExact": "{watts} W pro Gerät aus dem Equipment-Katalog: {name}.",
    "listTables.powerLoading": "Leistungsdaten werden geladen…",
    "listTables.powerError": "Equipment-Katalog nicht verfügbar — Gesamtleistung nicht berechnet.",
    "listTables.notFoundInCatalog": "Nicht im Katalog gefunden: {names}",

    // --- power summary (editor/power.ts — shared by the Gear tab AND the printed rider) --------------------
    "power.noGearItems": "Noch kein Equipment vorhanden.",
    "power.totalLine": "Bühne / FOH: {backline} W · Beschallungsanlage (PA): {pa} W",
    "power.fromLine": "— berechnet für {resolved} von {items} Einträgen.",
    "picker.catalogLoading": "Katalog wird geladen…",
    "picker.samePurpose": "gleicher Einsatz",
    "picker.family": "verwandt",
    "picker.startTyping": "Modell oder Hersteller eingeben",
    "picker.keepTyped": "«{text}» so lassen, wie geschrieben",
    "picker.classAnswers": "Modelle, die «{name}» entsprechen",
    "gear.notInCatalog": "nicht im Katalog",
    "power.itemSingular": "{n} Eintrag",
    "power.itemPlural": "{n} Einträge",
    "power.zeroPowerSingular": "{items} benötigt keinen Netzstrom.",
    "power.zeroPowerPlural": "{items} benötigen keinen Netzstrom.",
    "power.unresolvedLine": "{items} nicht im Katalog gefunden (nicht mitberechnet).",

    // --- print document (editor/print-rider.ts) -------------------------------------------------------------
    "printRider.noInputChannels": "Keine Eingangskanäle.",
    "printRider.noOutputChannels": "Keine Ausgangskanäle.",
    "printRider.noGearItems": "Kein Equipment.",
    "printRider.headers.ch": "Ch",
    "printRider.headers.v48": "48V",
    "printRider.headers.source": "Quelle",
    "printRider.headers.micDi": "Mikrofon / DI",
    "printRider.headers.type": "Typ",
    "printRider.headers.owner": "Eigentümer",
    "printRider.headers.comment": "Bemerkung",
    "printRider.headers.send": "Aux-Send",
    "printRider.headers.category": "Kategorie",
    "printRider.headers.name": "Name",
    "printRider.headers.count": "Anzahl",
    "printRider.headers.belongsTo": "Gehört zu",
    "printRider.headers.offer": "OFFER",
    "printRider.gear.bandBrings": "Bringt die Band selbst — nicht bestellen",
    "printRider.gear.allCables": "Alle Kabel, die zum Anschluss der in diesem Rider genannten Technik nötig sind",
    "printRider.gear.bandNote": "Dieses Equipment ist auf der Bühne: Die Band bringt es selbst mit. Hier ist nichts zu stellen.",
    "printRider.gear.ungrouped": "Zu keinem Set gehörig",
    "printRider.gear.noCategory": "Ohne Kategorie",
    "gearList.section.pa": "Beschallung (P.A.)",
    "gearList.section.amplification": "Verstärkung",
    "gearList.section.mixing": "Mischsystem",
    "gearList.section.backline": "Backline",
    "gearList.section.monitoring": "Monitoring",
    "gearList.section.instruments": "Instrumente",
    "gearList.section.mics": "Mikrofone und DI",
    "gearList.section.stands": "Stative",
    "gearList.section.stage": "Bühnenbau",
    "gearList.section.power": "Stromverteilung",
    "gearList.section.wire": "Kabel",
    "gearList.section.consumables": "Verbrauchsmaterial",
    "gearList.standFor.microphone": "Mikrofonstative",
    "gearList.standFor.guitar-bass": "Gitarren- und Bassständer",
    "gearList.standFor.keyboard": "Keyboardständer",
    "gearList.standFor.wind": "Ständer für Blasinstrumente",
    "gearList.standFor.drums-percussion": "Schlagzeug- und Percussionstative",
    "gearList.standFor.speaker": "Lautsprecherstative",
    "gearList.standFor.sheet-music-other": "Notenpulte und Sonstiges",
    "gearList.drumKit": "Schlagzeug",
    "gearList.otherInstruments": "Weitere Instrumente",
    "gearList.drumKitMics": "Schlagzeug-Mikrofone",
    "gearList.otherMics": "Weitere Mikrofone und DI",
    "publicViewer.gear.eachFor": "Jedes der {n} — wofür es dient",
    "gearList.bandBlock": "Bringt die Band mit",
    "gearList.where.standalone": "Einzelposition",
    "gearList.where.standaloneMany": "{count} Einzelpositionen",
    "gearList.where.acrossStage": "{count} Stellen auf der Bühne",
    "gearList.alsoUnder": "auch unter {section}: {count}",
    "gearList.derivedFrom": "aus der Input-Liste, Kanal {channels} · {source} — wird in der Input-Liste geändert",
    "gearList.groupCount": "{lines} Zeilen · {items} Stk.",
    "gearList.groupCountOne": "1 Zeile · {items} Stk.",
    "gearList.unstated.pa": "In diesem Rider nicht angegeben.",
    "gearList.unstated.mixing": "In diesem Rider nicht angegeben — aus Input- und Output-Liste berechnet.",
    "gearList.unstated.band": "Die Band bringt ihr eigenes mit — am Ende der Liste.",
    "gearList.mixing.need": "Input-Liste: {inputs}. Output-Liste: {outputs}.",
    "gearList.mixing.inputs.one": "{n} Eingang",
    "gearList.mixing.inputs.few": "{n} Eingänge",
    "gearList.mixing.inputs.many": "{n} Eingänge",
    "gearList.mixing.inputs.other": "{n} Eingänge",
    "gearList.mixing.outputs.one": "{n} Ausgang",
    "gearList.mixing.outputs.few": "{n} Ausgänge",
    "gearList.mixing.outputs.many": "{n} Ausgänge",
    "gearList.mixing.outputs.other": "{n} Ausgänge",
    "gearList.mixing.modelUnstated": "Modell nicht angegeben.",
    "gearList.mixing.modelUnstatedBare": "Modell nicht angegeben",
    "gearList.mixing.requirement": "Mischsystem mit einer Kapazität von mindestens {inputs} / {outputs}: {picks}.",
    "gearList.mixing.monitorRequirement": "Monitorpult mit einer Kapazität von mindestens {inputs} / {outputs}: {picks}.",
    "gearList.mixing.pickBrand": "ein beliebiges {brand}-Pult",
    "mixing.line.title": "Mischsystem",
    "mixing.monitorDesk.title": "Monitorpult",
    "mixing.monitorConsole.title": "Monitorpult",
    "mixing.monitor.fromFoh": "Vom FOH",
    "mixing.monitor.separate": "Separates Pult",
    "mixing.need.short": "≥{inputs} Ein / {outputs} Aus",
    "mixing.notSpecified": "Modell nicht angegeben",
    "mixing.line.tooltip": "{title}: {picks} — zum Bearbeiten klicken",
    "mixing.dialog.need": "Mindestens {inputs} Eingänge / {outputs} Ausgänge (aus den Listen)",
    "mixing.dialog.close": "Schließen",
    "mixing.dialog.remove": "{name} entfernen",
    "mixing.dialog.done": "Fertig",
    "mixing.dialog.digital": "Digital",
    "mixing.dialog.analogue": "Analog",
    "mixing.dialog.brandsHint": "Marke wählen. Jedes ihrer Pulte, das die Show trägt, ist recht — oder die Marke öffnen und genaue Pulte wählen.",
    "mixing.dialog.back": "‹ Marken",
    "mixing.dialog.anyBrand": "Beliebiges {brand}-Pult",
    "mixing.dialog.anyBrandMeta": "alle, die die Show tragen: {fit} von {total}",
    "mixing.tier.senior": "Oberklasse",
    "mixing.tier.mid": "Mittelklasse",
    "mixing.tier.entry": "Einstieg",
    "mixing.desk.figures": "{inputs} Ein / {outputs} Aus",
    "mixing.desk.unknown": "?",
    "mixing.desk.notStated": "Kapazität nicht angegeben",
    "mixing.desk.tooSmall": "zu klein für diesen Rider",
    "mixing.desk.discontinued": "nicht mehr gebaut",
    "mixing.pick.brand": "Beliebiges {brand}",
    "gearList.bySection": "Nach Bereich",
    "gearList.byKit": "Nach Set",
    "gearList.groupingLabel": "Gruppieren",
    "gearList.collapseAll": "Alle einklappen",
    "gearList.expandAll": "Alle ausklappen",
    "printRider.gear.orderSummary": "Zu liefern: {lines} Zeilen, {items} Stk. in {sections} Bereichen.",
    "printRider.gear.orderSummaryBand": "Weitere {lines} Zeilen bringt die Band selbst mit: Sie stehen am Ende und sind nicht zu liefern.",
    "printRider.gear.youOffer": "Ihr Angebot:",
    "printRider.headers.offerHint": "✓ wie angefragt, oder gleichwertig",
    "printRider.offer.date": "Datum",
    "printRider.offer.city": "Stadt",
    "printRider.offer.venue": "Spielstätte",
    "printRider.offer.company": "Verleihfirma",
    "printRider.offer.answeredBy": "Beantwortet von",
    "printRider.offer.weAsk": "Wir bitten um",
    "printRider.offer.youOffer": "Sie bieten an",
    "printRider.gear.derivedFrom": "aus der Input-Liste, Kanal {channels}",
    "printRider.gear.summaryLabel": "Übersicht",
    "printRider.gear.summaryOr": "oder:",
    "printRider.gear.inDetail": "Im Einzelnen",
    "printRider.gear.sum.wedgeMixes": "Wedge-Mixe: {count}",
    "printRider.gear.sum.iemMixes": "In-Ear-Mixe: {count}",
    "printRider.gear.sum.otherOutputs": "Weitere Ausgänge: {count}",
    "printRider.gear.sum.microphones": "Mikrofone: {count}",
    "printRider.gear.sum.microphonesWireless": "Mikrofone: {count}, davon Funk: {wireless}",
    "printRider.gear.sum.diBoxes": "DI-Boxen: {count}",
    "printRider.gear.sum.drumKit": "Schlagzeug, Teile: {pieces}",
    "printRider.gear.sum.more": "und {count} weitere",
    "printRider.offer.heading": "Auf diesen Rider wird im Dokument selbst geantwortet",
    "printRider.offer.howTitle": "So antworten Sie.",
    "printRider.offer.howTick": "angekreuzt — genau wie gewünscht",
    "printRider.offer.howEq": "die Linie — das angebotene Äquivalent",
    "printRider.offer.howSummary": "Jeder Equipment-Abschnitt beginnt mit einer Übersicht: Kreuzen Sie sie an, wenn der ganze Abschnitt wie gewünscht ist, und antworten Sie nur dort Zeile für Zeile, wo Ihr Angebot abweicht.",
    "printRider.offer.howNo": "Was Sie nicht stellen können: „nein“ und den Grund schreiben. Ein Grund ist eine Antwort, Schweigen nicht.",
    "printRider.offer.stageAsk": "Bühne {width} × {length} m.",
    "printRider.offer.signatures": "Unterschriften",
    "printRider.offer.preparedBy": "Angebot erstellt von (Verleih / Venue)",
    "printRider.offer.acceptedBy": "Für die Band angenommen",
    "printRider.offer.signDate": "Datum",
    "printRider.offer.answerOk": "OK",
    "printRider.offer.answerSub": "Ersatz: {model}",
    "printRider.offer.answerBandCarried": "Bringt die Band mit",
    "printRider.offer.answerNa": "Nicht verfügbar: {reason}",
    "printRider.offer.answerVenue": "Vom Veranstaltungsort",
    "printRider.offer.answerVenueNote": "Vom Veranstaltungsort: {note}",
    "printRider.offer.answeredHeading": "Angebot von {supplier} · {status} · Antworten v{round}",
    "printRider.footer.offerFor": "Angebot für: ______________________",
    "printRider.footer.page": "Seite {page} von {pages}",
    "printRider.publicLinkMissing":
        'Öffentlicher Link: noch nicht veröffentlicht — nutzen Sie den Button „Rider veröffentlichen“ in der oberen Leiste für einen Link mit QR-Code.',
    "printRider.publicLinkLabel": "Öffentlicher Link (Scannen oder Aufrufen) — schreibgeschützt, keine Anmeldung nötig — v{version}",
    "printRider.qrUnavailable": "QR-Code nicht verfügbar (URL zu lang) — nutzen Sie die URL oben.",
    "printRider.qrAriaLabel": "QR-Code zur öffentlichen Seite dieses Tech Riders",
    "printRider.untitledRider": "Unbenannter Rider",
    "printRider.stagePlotUnavailable": "Bühnenplan nicht verfügbar.",
    "printRider.stagePlotPending": "Bühnenaufnahme wird erstellt…",
    "printRider.stagePlanPending": "Bühnenplan wird erstellt…",
    "printRider.stagePlotAlt": "Bühnenplan",
    "printRider.sections.stagePlan": "Bühnenplan — Draufsicht",
    "printRider.stagePlanAlt": "Bühnenplan, Draufsicht",
    "printRider.stagePlanUnavailable": "Bühnenplan nicht verfügbar.",
    "printRider.contactLabel": "Kontakt: {name}",
    "printRider.telLabel": "Tel: {phone}",
    "printRider.emailLabel": "E-Mail: {email}",
    "printRider.bandLogoAlt": "Bandlogo",
    "printRider.footerPage": "Seite {page} von {pages}",
    "printRider.docTitle": "{heading} — Tech Rider",
    "printRider.metaBrand": "SoundCheckBox Tech Rider",
    "printRider.idLabel": "ID: {id}",
    "printRider.unsavedId": "(ungespeichert)",
    "printRider.stageSize": "Bühne: {width} × {length} m",
    "printRider.sections.stagePlot": "Bühnenplan (Stage plot)",
    "printRider.sections.riderNotes": "Anmerkungen zum Rider",
    "printRider.sections.inputList": "Eingangsliste (Input list)",
    "printRider.sections.outputList": "Ausgangsliste (Output list)",
    "printRider.sections.gearList": "Equipment-Liste (Gear list)",
    // --- print settings panel (components/print-options-dialog.ts, SCRUM-96) ---
    "printVariant.heading": "Was soll gedruckt werden?",
  "printVariant.simpleName": "Simple View",
  "printVariant.simpleNote": "Eine Querformatseite: der Bühnenplan, darunter Input- und Output-Liste.",
  "printVariant.techName": "Tech stage plot",
  "printVariant.techNote": "Eine Seite, Schwarz-Weiß-Plan — für die Technik",
  "printVariant.bothName": "Beide Blätter",
  "printVariant.bothNote": "Zwei Seiten: Schwarz-Weiß für die Technik, Farbe für den Künstler",
  "printVariant.fullName": "Alles drucken",
  "printVariant.fullNote": "Der vollständige Rider, jeder Abschnitt auf einem eigenen Blatt.",
  "print.fileNameSimple": "{title} — Simple View",
  "print.fileNameTech": "{title} — Tech Plot",
  "print.fileNameBoth": "{title} — Tech Plot + Simple View",
  "print.fileNameFull": "{title} — Full Details",
  "simpleView.captionSuffix": "Interaktiven Bühnenplan ansehen und ein Angebot für diesen Rider abgeben auf soundcheckbox.com",
  "simpleView.labelTech": "Tech stage plot",
  "simpleView.labelColour": "Simple view",
  "simpleView.captionUnpublished": "Nicht veröffentlichter Rider — {site}",
  "editor.documentTitle": "{title} — SoundCheckBox",
  "editor.documentTitleUntitled": "Rider ohne Titel",
  "simpleView.plotUnavailable": "Bühnenplan nicht verfügbar",
  "simpleView.overflow": "Einige Zeilen haben nicht auf dieses Blatt gepasst.",
  "simpleView.textClipped": "Der übrige Text des Riders steht im vollständigen gedruckten Rider und auf seiner öffentlichen Seite.",
  "printOptions.heading": "Druckeinstellungen",
    "printOptions.openTitle": "Druckeinstellungen…",
    "printOptions.hint": "Wählen Sie, was auf die Seite kommt. Lassen Sie eine Spaltenüberschrift leer, um den Standardnamen zu behalten.",
    "printOptions.presetFull": "Vollständiger Rider",
    "printOptions.presetOnePage": "Eine Seite (Querformat)",
    "printOptions.gearLegend": "Equipment-Liste",
    "printOptions.gearByCategory": "Nach Kategorie gruppieren",
    "printOptions.gearByKit": "Nach Set gruppieren",
    "printOptions.pageLegend": "Seite",
    "printOptions.pageBreaks": "Jeder Abschnitt auf eigener Seite",
    "printOptions.landscapePage": "Querformat-Seite",
    "printOptions.alwaysLandscape": "Immer Querformat, ganze Seite",
    "printOptions.onCover": "Auf dem Deckblatt",
    "printOptions.coverInfo": "Informationen",
    "printOptions.coverEditContacts": "Bearbeiten",
    "printOptions.coverSaveContacts": "Speichern",
    "printOptions.coverBandLink": "Band-Link",
    "printOptions.coverOff": "wird nicht gedruckt",
    "printOptions.coverMissing": "Leer: {fields}",
    "printOptions.coverMissingShort": "leer: {count}",
    "printOptions.sectionsLegend": "Abschnitte",
    "printOptions.lookLegend": "Gestaltung",
    "printOptions.coverLegend": "Deckblatt",
    "printOptions.cover": "Deckblatt drucken",
    "printOptions.coverLogoSize": "Logogröße",
    "printOptions.coverTextSize": "Schriftgröße",
    "printOptions.coverLogoFile": "Logo",
    "printOptions.coverNoLogo": "nicht hochgeladen",
    "printOptions.coverLogoUpload": "Hochladen",
    "printOptions.coverLogoReplace": "Ersetzen",
    "printOptions.coverLogoClear": "Entfernen",
    "printOptions.coverLogoFromRider": "Das Logo liegt im Rider — dasselbe erscheint auf der öffentlichen Seite und im Kontaktblock",
    "printOptions.coverCheckEmpty": "leer",
    "printOptions.coverCheck.logo": "Logo",
    "printOptions.coverCheck.band": "Band",
    "printOptions.coverCheck.rider": "Rider",
    "printOptions.coverCheck.contact": "Kontakte",
    "printOptions.coverCheck.link": "Band-Link",
    "printOptions.coverCheck.qr": "QR des öffentlichen Links",
    "printOptions.coverLogo": "Logo",
    "printOptions.coverText": "Text",
    "printOptions.align.left": "Links",
    "printOptions.align.center": "Zentriert",
    "printOptions.align.right": "Rechts",
    "printOptions.previewTitle": "Vorschau",
    "printOptions.previewBuilding": "Vorschau wird erstellt…",
    "printOptions.previewFailed": "Vorschau nicht verfügbar",
    "printOptions.style.plain": "Schlicht",
    "printOptions.style.classical": "Klassik",
    "printOptions.style.jazz": "Jazz",
    "printOptions.style.rock": "Rock",
    "printOptions.style.metal": "Metal",
    "printOptions.style.disco": "Disco",
    "printOptions.palette.mono": "Schwarzweiß",
    "printOptions.palette.graphite": "Graphit",
    "printOptions.palette.blue": "Blau",
    "printOptions.palette.red": "Rot",
    "printOptions.palette.amber": "Bernstein",
    "printOptions.palette.violet": "Violett",
    "printOptions.inputColumnsLegend": "Spalten der Input-Liste",
    "printOptions.outputColumnsLegend": "Spalten der Output-Liste",
    "printOptions.confirm": "Drucken",
    "printOptions.savePdf": "Als PDF speichern",
    "printOptions.savedPdf": "PDF gespeichert.",
    "printOptions.section.contact": "Band und Kontakte",
    "printOptions.section.publicLink": "Öffentlicher Link und QR",
    "printOptions.section.powerSummary": "Gesamtleistung",
    "printOptions.section.stage3d": "Stage 3D view (in color)",
    "printOptions.section.techTopView": "Stage plot tech top view",

    // --- wysiwyg toolbar (components/wysiwyg-editor.ts) --------------------------------------------------
    "wysiwyg.bold": "Fett",
    "wysiwyg.italic": "Kursiv",
    "wysiwyg.underline": "Unterstrichen",
    "wysiwyg.bulletedList": "Aufzählung",
    "wysiwyg.numberedList": "Nummerierte Liste",
    "wysiwyg.clearFormatting": "Formatierung löschen",
    "wysiwyg.boldLabel": "B",
    "wysiwyg.italicLabel": "I",
    "wysiwyg.underlineLabel": "U",
    "wysiwyg.bulletedListLabel": "• Liste",
    "wysiwyg.numberedListLabel": "1. Liste",
    "wysiwyg.clearFormattingLabel": "⌫ Löschen",

    // --- main.ts fatal startup overlay --------------------------------------------------------------------
    "main.fatal.heading": "SoundCheckBox konnte nicht gestartet werden",
    "main.fatal.hint": "Senden Sie diesen Text an den Support — er hilft dabei, das Problem exakt zu lokalisieren.",
    "main.fatal.userAgentLabel": "User-Agent: {ua}",

    // --- editor tour & cheatsheet (components/editor-tour.ts) ---------------------------------------------
    "tour.buttonTitle": "Interaktive Tour & Hilfe (?)",
    "tour.buttonLabel": "Hilfe",
    "tour.stepBadge": "Schritt {current} von {total}",
    "tour.closeAriaLabel": "Schließen",
    "tour.prev": "← Zurück",
    "tour.next": "Weiter →",
    "tour.finish": "Fertig",
    "tour.cheatSheetButton": "Spickzettel",
    "tour.cheatSheetButtonTitle": "Vollständige Tastatur- & Modifikatoren-Übersicht anzeigen",
    "tour.cheatSheetTitle": "Editor-Tastenkürzel & Modifikatoren",
    "tour.startTourButton": "Tour starten",
    "tour.closeSheetButton": "Schließen",

    "tour.steps.stageSize.title": "1. Bühnengröße ändern",
    "tour.steps.stageSize.desc": "Doppelklicken Sie auf den Bühnenboden (auf dem Touchscreen: doppelt tippen). Ziehen Sie die Griffe an den Kanten und Ecken, um Breite und Tiefe der Bühne einzustellen; die Größe in Metern steht oben links. Klicken Sie auf <b>Fertig</b> oder drücken Sie <b>Enter</b>.",
    "tour.steps.addObject.title": "2. Musiker und Equipment hinzufügen",
    "tour.steps.addObject.desc": "Wählen Sie unten einen Reiter (<b>members</b> für die Musiker, <b>Drums &amp; Perc</b>, <b>mic &amp; DI</b>, <b>monitors</b>, <b>backline</b>…). Klicken Sie auf ein Preset oder ziehen Sie es auf die Bühne, dann ziehen Sie es an seinen Platz.",
    "tour.steps.addObject.iem": "Manche Musiker bevorzugen In-Ears.",
    "tour.steps.addObject.outputs": "Die <b>Output List</b> wird automatisch neu berechnet.",
    "tour.steps.members.title": "3. Musiker und ihr Equipment",
    "tour.steps.members.desc": "Ein Musiker (<b>members</b>) ist die Basis eines Presets: Seine Instrumente, Mikrofone und DIs kommen mit ihm. Manchmal ist es praktischer, auf der Bühne nur das Equipment zu sehen: Halten Sie <b>Alt</b>, klicken Sie auf den Musiker und drücken Sie <b>Delete</b> — das Equipment bleibt.",
    "tour.steps.arrange.title": "4. Bühne einrichten",
    "tour.steps.arrange.desc": "Ziehen Sie die Objekte an ihren Platz. <b>Drehen</b> blendet einen Ring ein: Ziehen Sie daran, um die Auswahl zu drehen. <b>Objekt anbinden</b> und danach ein Klick auf ein Podest oder ein Stativ befestigt das Objekt daran, sodass beide sich gemeinsam bewegen. <b>Ganzer Stapel</b> / <b>Einzelnes Objekt</b> bestimmt, was ein Klick erfasst. Gesten: <b>Spickzettel</b>. Die Lage von Equipment und Bühnenaufbauten zueinander lässt sich manchmal leichter in der <b>Draufsicht</b> feinjustieren.",
    "tour.steps.channels.title": "5. Input List und Output List",
    "tour.steps.channels.desc": "<b>Input List</b> und <b>Output List</b> entstehen automatisch aus dem, was auf der Bühne steht; tragen Sie nur die Namen der Musiker in die Spalte <b>Quelle</b> ein. Ziehen Sie eine Zeile, um ihren <b>Ch</b> zu ändern. Geben Sie den Mixen ihre Adressen: Tragen Sie in <b>Quelle</b> ein, für wen jeder Monitor aufgestellt ist.",
    "tour.steps.micDi.title": "6. Mikrofone und DIs",
    "tour.steps.micDi.desc": "Mikrofone und DIs kommen mit den Presets. Klicken Sie in einer Zeile auf die Zelle <b>Mikro / DI</b> und wählen Sie im Dropdown die, die Sie bevorzugen.",
    "tour.steps.planSheet.title": "7. Planblatt prüfen",
    "tour.steps.planSheet.desc": "Wechseln Sie zum <b>Planblatt</b> und prüfen Sie, wie Ihr Rider als Zeichnung aussieht: So sieht ihn die Crew auf der Bühne. Objekte lassen sich auch hier verschieben.",
    "tour.steps.riderFill.title": "8. Rider ausfüllen und speichern",
    "tour.steps.riderFill.desc": "Rechts im Reiter <b>Rider</b>: Schreiben Sie den <b>Rider-Text / Anmerkungen</b>, tragen Sie <b>Titel</b> und <b>Bandname</b> ein und fügen Sie das Bandlogo unter <b>Logo-Datei</b> hinzu. Klicken Sie dann oben auf <b>Rider in Datenbank speichern</b>.",
    "tour.steps.publishPrint.title": "9. Veröffentlichen und drucken",
    "tour.steps.publishPrint.desc": "Ist der Rider gespeichert, erstellt <b>Rider veröffentlichen</b> einen Nur-Lese-Link für die Venue und kopiert ihn. <b>PDF</b> fragt <b>Was soll gedruckt werden?</b>: <b>Simple View</b>, <b>Tech stage plot</b> oder <b>Beide Blätter</b>; <b>Alles drucken</b> öffnet die <b>Druckeinstellungen</b> mit Vorschau.",
    "tour.end.title": "Bühne leeren oder weitermachen?",
    "tour.end.desc": "Die Tour hat Equipment auf die Bühne gestellt. Die Bühne leeren oder mit dieser Bühne weiterarbeiten?",
    "tour.end.clean": "Bühne leeren",
    "tour.end.keep": "Mit dieser Bühne weitermachen",
    "tour.banner.text": "Zum ersten Mal hier? Machen Sie eine kurze Tour: Das Programm zeigt jeden Schritt auf der Bühne.",
    "tour.banner.start": "Tour starten",
    "tour.banner.close": "Schließen",

    "tour.cs.sectionNav": "3D-Navigation",
    "tour.cs.dragOrbit": "Kameraansicht drehen",
    "tour.cs.dragPan": "Bühnenebene verschieben",
    "tour.cs.zoom": "Vergrößern / Verkleinern",
    "tour.cs.topView": "Draufsicht / 3D umschalten",

    "tour.cs.sectionModifiers": "Gesten & Modifikatoren",
    "tour.cs.clickDefault": "Gruppe oder Podest auswählen und verschieben",
    "tour.cs.altClick": "Einzelnes Objekt in Gruppe/Podest auswählen",
    "tour.cs.altDrag": "Einzelnes Objekt ohne Gruppe verschieben",
    "tour.cs.shiftGlue": "Gezogenes Objekt an Ziel anheften",
    "tour.cs.altShiftDetach": "Objekt aus Gruppe lösen",
    "tour.cs.rotateGizmo": "Gruppe drehen (Alt zum Drehen eines Objekts)",

    "tour.cs.sectionObjectOps": "Objekt-Operationen",
    "tour.cs.opProperties": "Objektparameter und Audiokanäle öffnen",
    "tour.cs.opRotate": "Interaktiven Rotationsring aktivieren",
    "tour.cs.opAttach": "Objekt an ein anderes binden",
    "tour.cs.opSavePreset": "Gruppe als Benutzer-Preset speichern",
    "tour.cs.opDelete": "Nur dieses Objekt löschen (Kinder bleiben erhalten)",
    "tour.cs.opDeleteTree": "Objekt samt allen Unterobjekten löschen",

    "tour.cs.sectionHotkeys": "Tastaturkürzel",
    "tour.cs.keyProperties": "Objekteigenschaften ein-/ausblenden",
    "tour.cs.keyRotate": "Rotationsring ein-/ausschalten",
    "tour.cs.keyInputList": "Input List ein-/ausblenden",
    "tour.cs.keyOutputList": "Output List ein-/ausblenden",
    "tour.cs.keyUndo": "Aktion rückgängig machen",
    "tour.cs.keyRedo": "Aktion wiederholen",
    "tour.cs.keySave": "Rider speichern",
    "tour.cs.keyEscape": "Auswahl aufheben / Dialog schließen",
    "sideNav.shows": "Shows",
    "shows.pageTitle": "Shows",
    "shows.tab.inbox": "Posteingang",
    "shows.tab.calendar": "Kalender",
    "shows.tabsLabel": "Bereiche der Shows",
    "shows.tab.list": "Shows",
    "shows.list.label": "Alle Shows",
    "shows.list.col.date": "Datum",
    "shows.list.col.band": "Band",
    "shows.list.col.venue": "Spielort",
    "shows.list.col.city": "Stadt",
    "shows.list.col.rental": "Verleih",
    "shows.list.col.status": "Status",
    "shows.list.empty": "Noch keine Shows.",
    "shows.list.emptyFiltered": "Keine Shows in dieser Auswahl.",
    "shows.list.rentalOne": "1 Angebot",
    "shows.list.rentalMany": "{n} Angebote",
    "shows.list.rentalNone": "Noch keine Angebote",
    "shows.list.rentalYou": "Sie",
    "shows.list.dateOpen": "Datum noch nicht festgelegt",
    "shows.list.state.sketch": "Entwurf",
    "shows.mcal.label": "Show-Kalender",
    "shows.mcal.prev": "Voriger Monat",
    "shows.mcal.next": "Nächster Monat",
    "shows.mcal.showAll": "Alle anzeigen",
    "shows.mcal.filterDay": "Shows am {day}",
    "shows.mcal.filterMonth": "Shows im {month}",
    "shows.mcal.today": "heute",
    "shows.loadFailed": "Deine Shows konnten nicht geladen werden: {error}",
    "shows.inbox.empty": "Im Moment braucht dich nichts.",
    "shows.inbox.listLabel": "Was dich braucht",
    "shows.inbox.unknownWho": "Ein Anbieter",
    "shows.inbox.approvalNew": "{who} hat ein Angebot für {subject} ({when}) geschickt. Es wartet auf deine Genehmigung.",
    "shows.inbox.approvalAgain": "{who} hat das Angebot für {subject} ({when}) erneut geschickt. Es wartet auf deine Genehmigung.",
    "shows.inbox.newOffer": "{who} hat ein Angebot für {subject} ({when}) geschickt.",
    "shows.inbox.reconfirm": "Der Rider hat sich geändert (v{from} auf v{to}). Bestätige dein Angebot für {subject} ({when}) erneut.",
    "shows.inbox.declined": "Dein Angebot für {subject} ({when}) wurde abgelehnt.",
    "shows.inbox.declinedReason": "Grund: {reason}",
    "shows.inbox.riderRequest": "Für {subject} ({when}) wurde ein Rider angefragt.",
    "shows.inbox.open": "Öffnen",
    "shows.inbox.dateOpen": "Datum noch nicht festgelegt",
    "shows.inbox.received": "Eingegangen {at}",
    "shows.inbox.new": "Neu",
    "sideNav.unreadCount": "{n} ungelesen",
    "shows.cal.month": "Monat",
    "shows.cal.week": "Woche",
    "shows.cal.list": "Liste",
    "shows.cal.today": "Heute",
    "shows.cal.prev": "Zurück",
    "shows.cal.next": "Weiter",
    "shows.cal.noEvents": "Keine Shows in diesem Zeitraum",
    "shows.cal.waiting": "Warten auf ein Datum",
    "shows.cal.waitingHint": "Shows ohne Datum stehen nicht im Raster.",
    "shows.cal.waitingEmpty": "Nichts wartet auf ein Datum.",
    "shows.cal.sketch": "Entwurf",
    "shows.cal.untitled": "Show ohne Titel",
    "shows.feed.title": "In deinem Kalender abonnieren",
    "shows.feed.hint": "Google-, Apple- und Outlook-Kalender akzeptieren diese Adresse über „Per URL abonnieren“.",
    "shows.feed.hidden": "Dein Link ist aktiv. Aus Sicherheitsgründen wird er nur bei der Erstellung angezeigt — erneuere ihn, um einen neuen zu erhalten.",
    "shows.feed.addressLabel": "Deine private Kalenderadresse",
    "shows.feed.create": "Link erstellen",
    "shows.feed.rotate": "Erneuern",
    "shows.feed.rotateConfirm": "Noch einmal klicken: Der alte Link funktioniert dann nicht mehr",
    "shows.feed.turnOff": "Ausschalten",
    "shows.feed.turnOffConfirm": "Noch einmal klicken zum Ausschalten",
    "shows.feed.failed": "Kalender-Link konnte nicht aktualisiert werden: {error}",
    "shows.detail.back": "Shows",
    "shows.detail.loadFailed": "Diese Show konnte nicht gelesen werden: {error}",
    "shows.detail.state.open": "Offen",
    "shows.detail.state.waiting": "Wartet auf Angebote",
    "shows.detail.state.offers_in": "Angebote eingegangen",
    "shows.detail.state.agreed": "Vereinbart",
    "shows.detail.state.past": "Vergangen",
    "shows.detail.acts": "Auftritte",
    "shows.detail.noActs": "Noch kein Act in dieser Show.",
    "shows.detail.riderVersion": "Rider v{version}",
    "shows.detail.noRider": "Noch kein Rider",
    "shows.detail.newerVersion": "Neuere Version verfügbar: v{version}",
    "shows.detail.getLink": "Show-Link anzeigen",
    "shows.detail.linkFailed": "Der Link konnte nicht geholt werden: {error}",
    "shows.detail.sketchTitle": "Entwurf — noch kein Datum, kein Ort",
    "shows.detail.sketchHint": "Lege ein Datum oder einen Ort fest, dann wird es eine normale Show.",
    "shows.detail.sketchWhere": "Datum und Ort",
    "shows.detail.change": "Datum und Ort ändern",
    "shows.detail.changeNeedsDate": "Wählen Sie ein Datum – die Show hat bereits eines.",
    "shows.detail.sketchSave": "Datum und Ort speichern",
    "shows.detail.sketchNeeds": "Wähle zuerst ein Datum oder einen Ort.",
    "shows.detail.saveFailed": "Speichern fehlgeschlagen: {error}",
    "shows.offers.heading": "Angebote",
    "shows.offers.empty": "Noch keine Angebote.",
    "shows.offers.sortLabel": "Sortieren nach",
    "shows.offers.sort.time": "Zeit (gesendet)",
    "shows.offers.sort.match": "Übereinstimmung",
    "shows.offers.sort.fewestMissing": "Am wenigsten fehlend",
    "shows.offers.sort.price": "Preis",
    "shows.offers.sort.priceNotYet": "noch nicht",
    "shows.offers.match": "{n} passen",
    "shows.offers.missing": "{n} fehlen",
    "shows.offers.riderChanged": "Rider geändert v{from} → v{to}",
    "shows.offers.open": "Angebot öffnen",
    "shows.offers.organiserApprove": "Als Veranstalter genehmigen",
    "shows.offers.organiserRequestChanges": "Als Veranstalter Änderungen anfordern",
    "shows.offers.actionFailed": "Das ging nicht: {error}",
    // --- crew: the hired worker's screen and the admin's test link (SCRUM-762) ---
    "crew.untitledShow": "Show",
    "crew.dateOpen": "Datum offen",
    "crew.venueNone": "Spielort offen",
    "crew.signInWhy": "Das ist ein Job bei einer Show. Melde dich an oder erstelle ein Konto, um ihn zu übernehmen – das dauert eine Minute.",
    "crew.signInToTake": "Anmelden und Job übernehmen",
    "crew.take": "Job übernehmen",
    "crew.takenBySomeone": "Jemand anderes hat diesen Job schon übernommen. Frag bei der Person, die dir den Link geschickt hat, nach deinem eigenen.",
    "crew.gone": "Diesen Job-Link gibt es nicht oder er wurde zurückgezogen.",
    "crew.failed": "Etwas ist schiefgelaufen: {error}",
    "crew.saveFailed": "Der Haken wurde nicht gespeichert: {error}",
    "crew.noRider": "Für diese Show gibt es noch keinen veröffentlichten Rider – es gibt nichts abzuhaken.",
    "crew.riderGone": "Der Rider dieser Show ist nicht mehr veröffentlicht, daher sind die Listen leer. Die Schritte bleiben.",
    "crew.riderMoved": "Der Rider wurde nach der Zusage für diese Show aktualisiert — diese Listen sind die neuen.",
    "crew.riderMovedOn": "Rider am {date} aktualisiert — neuer als das für diese Show Vereinbarte. Diese Listen sind die neuen.",
    "crew.tab.out": "Rüsten",
    "crew.tab.venue": "Spielort",
    "crew.tab.return": "Rückgabe",
    "crew.tab.plan": "Plan",
    "crew.time.warehouse": "Lager",
    "crew.time.truck": "LKW",
    "crew.time.venue": "Spielort",
    "crew.time.showRun": "Show",
    "crew.time.strike": "Abbau",
    "crew.thisPart": "Dieser Teil",
    "crew.doneOf": "{done} von {total}",
    "crew.done": "Erledigt",
    "crew.twoPeople": "Zu zweit tragen",
    "crew.showRunTwo": "2 Personen",
    "crew.kit.inside": "Was drin ist",
    "crew.kit.cablesApart": "Kabel werden im Block „Kabel“ gezählt",
    "crew.kit.included": "im Karton",
    "crew.kit.consumablesApart": "Batterien und Verbrauchsmaterial werden in einer eigenen Zeile gezählt",
    "crew.truckFront": "LKW-FRONT",
    "crew.missing": "Fehlt",
    "crew.broken": "Defekt",
    "crew.missingOpen": "FEHLT – bleibt offen",
    "crew.brokenOpen": "DEFEKT – bleibt offen",
    "crew.less": "Eins weniger",
    "crew.more": "Eins mehr",
    "crew.tickAria": "{name}: {done} von {total}",
    "crew.hint.fromOffer": "Nicht in diesem Rider – kommt aus dem Angebot des Verleihers",
    "crew.hint.nothing": "Hier ist nichts",
    "crew.returnOpen": "{n} Zeilen noch offen (nicht zurück, fehlen oder defekt). Sie bleiben auf der Liste des Verleihers – nichts verschwindet.",
    "crew.returnAllBack": "Alles ist zurück.",
    // SCRUM-760: «Loading done — depart» gate. Translated like the neighbouring crew.* keys.
    "crew.gate.depart": "Verladen fertig — abfahren",
    "crew.gate.departVenue": "Verladen — Spielort verlassen",
    "crew.gate.confirm": "LKW fährt jetzt los? Noch einmal drücken",
    "crew.gate.waiting": "Warten auf den Fahrer zur Abfahrt",
    "crew.gate.departed": "Abgefahren {time} · {name}",
    "crew.gate.departedNoName": "Abgefahren {time}",
    "crew.gate.by.driver": "Vom Fahrer losgeschickt",
    "crew.gate.by.supplier": "Von der Verleihfirma losgeschickt",
    "crew.gate.locked": "Der LKW ist abgefahren — das Verladen ist geschlossen. Bitte den Fahrer, es wieder zu öffnen, falls etwas vergessen wurde.",
    "crew.gate.next": "Weiter — {tab}",
    "crew.gate.reopen": "Verladen wieder öffnen",
    "crew.gate.failed": "Die Abfahrt konnte nicht aktualisiert werden: {error}",
    "crew.zoom": "Zoom",
    "crew.fit": "Einpassen",
    "crew.legendDone": "erledigt {n}",
    "crew.legendPart": "teilweise {n}",
    "crew.legendTodo": "offen {n}",
    "crew.legendMiss": "fehlt / defekt {n}",
    "crew.planHint": "Haken werden in Blöcken gesetzt; der Plan zeigt sie nach Ebenen, in der Reihenfolge des Tages.",
    "crew.changeover.title": "Umbauten zwischen den Auftritten",
    "crew.changeover.heading": "Umbau {from} → {to}",
    "crew.changeover.headingMin": "Umbau {from} → {to} ({n} Min.)",
    "crew.changeover.none": "Nichts zu bewegen: Was der nächste Auftritt braucht, steht schon an seinem Platz.",
    "crew.changeover.recheck": "Danach Line-Check, Kanäle: {channels}",
    "crew.zone.USR": "Hinten rechts",
    "crew.zone.USC": "Hinten Mitte",
    "crew.zone.USL": "Hinten links",
    "crew.zone.SR": "Rechts",
    "crew.zone.CS": "Bühnenmitte",
    "crew.zone.SL": "Links",
    "crew.zone.DSR": "Vorne rechts",
    "crew.zone.DSC": "Vorne Mitte",
    "crew.zone.DSL": "Vorne links",
    "crew.step.toWingsStand": "{item} samt Stativ hinter die Bühne bringen, Kabel bleibt dran",
    "crew.step.rollOut": "{item} hinter die Bühne rollen",
    "crew.step.back": "{item} von hinter der Bühne zurückbringen",
    "crew.step.swapStand": "{from} abnehmen, {to} auf dasselbe Stativ setzen",
    "crew.step.replace": "{from} durch {to} ersetzen",
    "crew.step.add": "{item} aufbauen",
    "crew.step.bandOut": "Equipment von {act} von der Bühne räumen: {items}",
    "crew.step.bandIn": "Equipment von {act} aufbauen: {items}",
    "crew.layer.picked": "Gepackt",
    "crew.layer.loaded": "Verladen",
    "crew.layer.placed": "Aufgestellt",
    "crew.layer.patched": "Gepatcht",
    "crew.layer.checked": "Geprüft",
    "crew.layer.unpatched": "Entpatcht",
    "crew.layer.struck": "Abgebaut",
    "crew.layer.loadedback": "Zurückverladen",
    "crew.layer.returned": "Zurück",
    "crew.phase.pick": "Im Lager zusammenstellen",
    "crew.phase.pickSub": "Reihenfolge des Entladens – der LKW wird umgekehrt beladen",
    "crew.phase.load": "LKW beladen",
    "crew.phase.loadSub": "Umgekehrte Reihenfolge · Schweres nach vorn an die LKW-Front",
    "crew.phase.setup": "Entladen und aufbauen",
    "crew.phase.setupSub": "Am Spielort",
    "crew.phase.showRun": "Show",
    "crew.phase.showRunSub": "Crew bleibt im Dienst – Bühnenaufgaben kommen später",
    "crew.phase.strike": "Abbau und Verladen",
    "crew.phase.strikeSub": "Nach der Show",
    "crew.phase.back": "Zurück im Lager",
    "crew.phase.backSub": "Entladen und einchecken – was zurück ist, was fehlt, was defekt ist",
    "crew.block.carts": "Wagen und Rampe",
    "crew.block.racks": "Racks und Strom",
    "crew.block.pa": "P.A.",
    "crew.block.mon": "Monitore",
    "crew.block.foh": "FOH",
    "crew.block.mics": "Mikrofone",
    "crew.block.dis": "DIs",
    "crew.block.stands": "Stative",
    "crew.block.backline": "Backline",
    "crew.block.risers": "Podeste und Möbel",
    "crew.block.cables": "Kabel",
    "crew.block.other": "Sonstiges",
    "crew.block.loadOut": "Verladen · {block}",
    "crew.block.unloadToPositions": "Auf Positionen entladen",
    "crew.block.unloadToPositionsSub": "Schweres direkt an den Platz, FOH an seine Position",
    "crew.block.paMonitorsInPlace": "P.A. und Monitore an Ort und Stelle",
    "crew.block.powerPa": "Strom P.A.",
    "crew.block.powerStage": "Strom Bühne",
    "crew.block.powerFoh": "Strom FOH",
    "crew.block.paCheck": "P.A.-Check und Front Fills",
    "crew.block.stageSet": "Bühne nach Rider",
    "crew.block.stageSetSub": "Parallel zum P.A.-Check",
    "crew.block.patch": "Patchen",
    "crew.block.patchSub": "Jeder Eingang der Inputliste",
    "crew.block.lineCheck": "Line Check",
    "crew.block.lineCheckSub": "Der Techniker der Band kommt und startet den Soundcheck",
    "crew.block.showRun": "Show",
    "crew.block.showRunSub": "Auf der Bühne während der Show",
    "crew.block.showRunN": "Show {n}",
    "crew.banner.powerOn": "Einschalten: zuerst die Quellen (Pult, Stagerack, Funk), dann die Verstärker.",
    "crew.banner.powerOff": "Ausschalten: zuerst die Verstärker, dann die Quellen (Pult, Stagerack, Funk).",
    "crew.block.unpatch": "Entpatchen",
    "crew.block.clearSmall": "Bühne räumen · klein, leicht und Kabel",
    "crew.block.clearSmallSub": "Zuerst Mikrofone, DIs, Stative, Kabel",
    "crew.block.clearLarge": "Bühne räumen · groß",
    "crew.block.clearLargeSub": "Verstärker, Podeste, Klavier, Monitore",
    "crew.block.fohPaLast": "FOH und P.A.",
    "crew.block.fohPaLastSub": "Zuletzt ab – beim Aufbau zuerst da",
    "admin.crew.title": "Crew-Link für dich selbst (Test)",
    "admin.crew.hint": "Trägt dich bei einer deiner Shows als Crew ein, damit du die Ansicht der Mitarbeitenden am Handy ausprobieren kannst. Öffne den Link am Handy und übernimm den Job.",
    "admin.crew.show": "Show",
    "admin.crew.make": "Crew-Link erstellen",
    "admin.crew.made": "Link erstellt. Öffne ihn am Handy.",
    "admin.crew.link": "Crew-Link",
    "admin.crew.open": "Öffnen",
    "admin.crew.noShows": "Du hast noch keine Shows – lege zuerst eine unter Shows an",
    "admin.crew.dateOpen": "kein Datum",
    "admin.crew.failed": "Das ging nicht: {error}",
    "admin.perf.title": "Einen Auftritt zu einer Show hinzufügen",
    "admin.perf.hint": "Macht daraus eine Show mit mehreren Auftritten: Der Tab „Spielort“ des Crew-Links zeigt dann die Umbauten zwischen den Auftritten. Nimmt die oben gewählte Show.",
    "admin.perf.name": "Name des Auftritts",
    "admin.perf.rider": "Rider (veröffentlicht)",
    "admin.perf.noRider": "Noch kein Rider",
    "admin.perf.add": "Auftritt hinzufügen",
    "admin.perf.added": "Hinzugefügt. Die Show hat jetzt {n} Auftritte.",
    "admin.perf.failed": "Das ging nicht: {error}",
    "admin.perf.needName": "Gib dem Auftritt einen Namen",

    // --- rental desk (rental/desk-answer-panel.ts, rental/desk-shows-panel.ts, pages/warehouse-tabs.ts, SCRUM-647) ---
    "rental.tabsLabel": "Bereiche des Lagerbestands",
    "rental.loadFailed": "Laden fehlgeschlagen: {error}",
    "rental.toAnswer.heading": "Zu beantworten",
    "rental.toAnswer.empty": "Nichts wartet auf Sie — alle Angebote sind beantwortet.",
    "rental.cover.positions": "{n} Positionen",
    "rental.cover.positionsOne": "{n} Position",
    "rental.cover.ok": "{n} aus deinem Lager",
    "rental.cover.short": "{n} zu wenig",
    "rental.cover.missing": "{n} fehlen",
    "rental.cover.answered": "beantwortet {n}",
    "rental.cover.emptyWarehouse": "Dein Lager ist leer – füge Geräte hinzu, um die Abdeckung zu sehen",
    "rental.reason.draft": "Noch nicht gesendet",
    "rental.reason.changes": "Änderungen erbeten",
    "rental.reason.reconfirm": "Rider geändert — neu bestätigen",
    "rental.shows.heading": "Meine Shows",
    "rental.shows.empty": "Noch keine Shows. Eine Show erscheint hier, sobald Sie ein Angebot für ein Datum senden.",
    "rental.shows.dateOpen": "Datum offen",
    "rental.shows.venueNone": "Ort offen",
    "rental.shows.myOffer": "Mein Angebot: {status}",
    "rental.crew.make": "Crew-Link",
    "rental.crew.making": "Wird erstellt…",
    "rental.crew.newLink": "Neuer Crew-Link — an einen Helfer senden",
    "rental.crew.linksHeading": "Crew-Links",
    "rental.crew.free": "Noch nicht übernommen",
    "rental.crew.takenBy": "Übernommen von {name}",
    "rental.crew.taken": "Übernommen",
    "rental.crew.failed": "Fehlgeschlagen: {error}",
    "rental.crew.withdraw": "Zurückziehen",
    "rental.crew.withdrawSure": "Zurückziehen? Noch einmal drücken",
    "rental.crew.withdrawFailed": "Der Link konnte nicht zurückgezogen werden: {error}",
    // SCRUM-760: driver link, role on a link row, the departure line. Translated like the neighbouring rental.crew.* keys.
    "rental.crew.driver": "Fahrer-Link",
    "rental.crew.role.crew": "Helfer",
    "rental.crew.role.driver": "Fahrer",
    "rental.crew.takeMyself": "Selbst übernehmen",
    "rental.crew.takenByYou": "Von dir übernommen",
    "rental.crew.openMine": "Öffnen",
    "rental.depart.out": "Vom Lager abgefahren",
    "rental.depart.venue": "Vom Spielort abgefahren",
    "rental.depart.reopen": "Verladen wieder öffnen",
    "rental.depart.failed": "Die Abfahrt konnte nicht aktualisiert werden: {error}",
    "rental.progress.heading": "Arbeitsfortschritt",
    "rental.tasks.heading": "Was die Crew macht",
    "rental.tasks.blocks": "{n} Blöcke",
    "rental.tasks.template": "Unsere Arbeitsreihenfolge",
    "rental.tasks.templateNote": "Die gleichen Schritte bei jeder Show. Eine konkrete Show ergänzt die Zahlen: was laut Rider zu holen, zu patchen und zu prüfen ist.",
    "rental.progress.doneOf": "{done} von {total}",
    "rental.progress.noLinks": "Noch kein Crew-Link — der Fortschritt erscheint, sobald ein Helfer abhakt.",
    "rental.progress.noRider": "Kein veröffentlichter Rider auf dieser Show — nur die Schritte zählen.",
    "rental.progress.failed": "Fortschritt konnte nicht geladen werden: {error}",
};